// CatalogoStreamingSection.js — Sección `catalogo_streaming`: la portada de
// Learning, catálogo de venta de cursos al estilo de una plataforma de streaming.
//
// - Cartelera a pantalla completa con el curso destacado (solo imagen: el
//   tráiler se ve en la vista previa de la miniatura y en la ficha).
// - Filas deslizables de miniaturas; al posar el cursor, la miniatura crece en
//   una vista previa con el tráiler y los botones (se pinta en un portal para
//   que el desplazamiento horizontal de la fila no la recorte), y la cartelera
//   pasa a mostrar ese curso: portada, nombre, descripción corta y enlace.
// - Fila «Top 10» con numerales gigantes y portada vertical.
// - «Continuar viendo» con la barra de avance (solo con sesión iniciada).
// - Ficha del curso en ventana: sinopsis, precio, docente y temario.
//
// Config: { items: [{ key, titulo, titulo_cartelera, sinopsis, especialidad,
//   nivel, horas, imagen, imagen_vertical, trailer, precio, precio_antes,
//   etiqueta, documento_id, url_compra, docente, color }],
//   filas: [{ titulo, tipo: 'items'|'top10'|'continuar'|'especialidad'|'novedades'|'categoria'|'titulo',
//             items[], especialidad, categoria, sin_categoria }],
//   · novedades: automática, los 10 cursos enlazados subidos más recientemente (el último primero),
//     en miniaturas como las demás filas, con su puesto en una muesca.
//   · categoria: los ítems con esa `categoria`; con `sin_categoria`, también los que no tienen.
//   · titulo: solo un título que agrupa las filas que siguen (p. ej. «Clasificación por habilidades»).
//   destacado_key, color_fondo, color_acento, color_texto, color_tenue, moneda,
//   texto_ver, texto_comprar, texto_info, texto_registro, texto_certificado }
// Un ítem con `documento_id` toma del curso real lo que no traiga escrito
// (título, sinopsis, portada, módulos y lecciones).
//
// Planes de formación (useMiPlan): un alumno con subrol especial solo ve los cursos de su
// subrol, en una única fila «Mis cursos obligatorios», con su avance en el plan arriba; el
// alumno Inactivo ve un aviso en lugar del catálogo. Sin subrol, todo sigue igual.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { BACKEND_BASE, mentor2Service } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import useMiPlan from '../../../hooks/useMiPlan';
import { fmtFechaHoraBogota } from '../../../utils/fecha';

const FUENTE = "'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
const TONOS_POSTER = ['#17385f', '#4b1839', '#0c4a48', '#5b2616', '#2b2862', '#3a4712', '#48233f', '#113f5c'];

const urlImg = (v) => (!v ? '' : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));
const sinHtml = (t) => String(t || '').replace(/<[^>]+>/g, ' ').replace(/(^|\n)\s*#{1,4}\s+/g, '$1').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
// Descripción (HTML o texto) → líneas: subtítulos «## », viñetas «• » y párrafos.
const lineasDescripcion = (t) => String(t || '').replace(/<h[1-6][^>]*>/gi, '\n## ').replace(/<li[^>]*>/gi, '\n• ').replace(/<[^>]+>/g, '\n')
  .replace(/&nbsp;/g, ' ').replace(/\n## \s*\n+/g, '\n## ').split(/\n+/).map(l => l.trim()).filter(Boolean);
// Sinopsis de respaldo (sin sinopsis propia): el primer párrafo de la descripción, cortado en ~260 caracteres.
const primerParrafo = (t) => {
  const p = lineasDescripcion(t).find(l => !/^(#{1,4}|[•\-–])\s/.test(l)) || '';
  if (p.length <= 280) return p;
  const corte = p.slice(0, 260); const punto = corte.lastIndexOf('. ');
  return punto > 120 ? corte.slice(0, punto + 1) : corte.replace(/\s+\S*$/, '') + '…';
};
const tonoDe = (txt) => {
  let h = 0;
  for (const ch of String(txt || 'x')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TONOS_POSTER[h % TONOS_POSTER.length];
};
const reduceMovimiento = () => typeof window !== 'undefined'
  && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const conPuntero = () => typeof window !== 'undefined'
  && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

// ============================================================
// ESTILOS
// ============================================================
const Raiz = styled.section`
  --fondo: ${p => p.$fondo};
  --acento: ${p => p.$acento};
  --sobre-acento: ${p => p.$sobreAcento};
  --texto: ${p => p.$texto};
  --tenue: ${p => p.$tenue};
  --superficie: color-mix(in srgb, var(--fondo) 82%, #ffffff 6%);
  --lateral: 4vw;
  background: var(--fondo);
  color: var(--texto);
  font-family: ${FUENTE};
  padding-bottom: 5rem;
  overflow: hidden;
  position: relative;
  button { font-family: inherit; }
  :focus-visible { outline: 2px solid var(--acento); outline-offset: 3px; }
`;

// ---------- Cartelera ----------
// Pantalla completa como mínimo; si el título o la sinopsis no caben, crece
// hacia abajo en vez de subirse al header (padding-top reserva su espacio).
const Cartelera = styled.div`
  position: relative;
  min-height: clamp(660px, 100vh, 1100px);
  padding-top: 7.5rem;
  box-sizing: border-box;
  display: flex;
  align-items: flex-end;
  @media (max-width: 700px) { min-height: 94vh; padding-top: 5.5rem; }
`;
const girarLuz = keyframes`to { transform: rotate(360deg); }`;
// La flecha sale por la derecha y entra otra desde la izquierda.
const avanzar = keyframes`from { transform: translateX(-50%); } to { transform: translateX(0); }`;
const ecualizar = keyframes`
  0% { transform: scaleY(.3); }
  50% { transform: scaleY(.85); }
  100% { transform: scaleY(.55); }
`;
const latido = keyframes`
  from { transform: scale(1); opacity: .75; }
  to { transform: scale(3.2); opacity: 0; }
`;
const acercar = keyframes`from { transform: scale(1.08) translate3d(1.5%, 0, 0); } to { transform: scale(1) translate3d(0, 0, 0); }`;
const Fondo = styled.div`
  position: absolute; inset: 0; overflow: hidden;
  > .capa { position: absolute; inset: 0; opacity: 0; transition: opacity 1.3s ease; }
  > .capa.on { opacity: 1; }
  > .capa.on > * { animation: ${acercar} 16s cubic-bezier(.2,.6,.3,1) both; }
  > .capa img { width: 100%; height: 100%; object-fit: cover; display: block; }
  @media (prefers-reduced-motion: reduce) { > .capa.on > * { animation: none; } > .capa { transition: none; } }
  &::after {
    content: ''; position: absolute; inset: 0; pointer-events: none;
    background:
      linear-gradient(to bottom, color-mix(in srgb, var(--fondo) 62%, transparent) 0%, transparent 16%),
      linear-gradient(to top, var(--fondo) 0%, color-mix(in srgb, var(--fondo) 50%, transparent) 13%, transparent 32%),
      linear-gradient(77deg, color-mix(in srgb, var(--fondo) 72%, transparent) 0%, color-mix(in srgb, var(--fondo) 28%, transparent) 34%, transparent 58%);
  }
  @media (max-width: 700px) {
    &::after { background: linear-gradient(to bottom, color-mix(in srgb, var(--fondo) 62%, transparent) 0%, transparent 14%), linear-gradient(to top, var(--fondo) 6%, color-mix(in srgb, var(--fondo) 45%, transparent) 40%, transparent 70%); }
  }
`;
const entrada = keyframes`from { opacity: 0; transform: translate3d(0, 18px, 0); } to { opacity: 1; transform: none; }`;
const CarteleraTexto = styled.div`
  position: relative; z-index: 2;
  padding: 0 var(--lateral) calc(9vw + 5.5rem);
  max-width: min(46rem, 92vw);
  animation: ${entrada} .9s cubic-bezier(.2,.7,.2,1) both;
  @media (prefers-reduced-motion: reduce) { animation: none; }
  @media (max-width: 700px) { padding-bottom: 2.5rem; text-align: center; margin: 0 auto; }
`;
const Especialidad = styled.p`
  margin: 0 0 .9rem; color: var(--texto); font-weight: 600; font-size: 1rem; letter-spacing: .01em;
  text-shadow: 0 1px 10px rgba(0,0,0,.55);
  display: flex; align-items: center; gap: .6rem;
  &::before { content: ''; width: 1.6rem; height: 2px; background: var(--acento); }
  @media (max-width: 700px) { justify-content: center; }
`;
const TituloCartelera = styled.h1`
  margin: 0 0 1.1rem; font-weight: 750; font-stretch: 80%;
  font-size: ${p => p.$largo ? 'clamp(2.3rem, 4.6vw, 4.4rem)' : 'clamp(2.8rem, 6.2vw, 6rem)'};
  line-height: .92; letter-spacing: .005em; text-wrap: balance;
  text-shadow: 0 2px 24px rgba(0,0,0,.35);
`;
const Meta = styled.div`
  display: flex; flex-wrap: wrap; gap: .35rem 1.1rem; align-items: center;
  font-size: .98rem; color: color-mix(in srgb, var(--texto) 85%, transparent); margin-bottom: 1rem;
  strong { color: var(--texto); font-weight: 700; }
  @media (max-width: 700px) { justify-content: center; }
`;
const Sinopsis = styled.p`
  margin: 0 0 1.6rem; font-size: clamp(1rem, 1.25vw, 1.2rem); line-height: 1.45;
  color: color-mix(in srgb, var(--texto) 88%, transparent); max-width: 36rem;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
  @media (max-width: 700px) { display: none; }
`;
const Botones = styled.div`
  display: flex; gap: .75rem; flex-wrap: wrap;
  @media (max-width: 700px) { justify-content: center; flex-wrap: nowrap; gap: .5rem; }
`;
const Boton = styled.button`
  display: inline-flex; align-items: center; gap: .6rem; cursor: pointer; border: 0;
  border-radius: 6px; padding: .78rem 1.6rem .78rem 1.25rem; font-size: 1.08rem; font-weight: 700;
  transition: background .2s ease, transform .15s ease;
  svg { width: 1.45em; height: 1.45em; flex: none; }
  &:active { transform: scale(.97); }
  ${p => p.$primario ? css`
    background: #fff; color: #05070b;
    &:hover { background: rgba(255,255,255,.78); }
  ` : css`
    background: rgba(109,118,133,.55); color: #fff; backdrop-filter: blur(6px);
    &:hover { background: rgba(109,118,133,.38); }
  `}
  small { font-weight: 500; opacity: .7; text-decoration: line-through; font-size: .8em; }
  @media (max-width: 700px) {
    flex: 1 1 0; justify-content: center; font-size: .95rem; padding: .7rem .8rem; white-space: nowrap;
  }
`;
const llenar = keyframes`from { transform: scaleX(0); } to { transform: scaleX(1); }`;
const Indicadores = styled.div`
  display: flex; gap: .5rem; margin-top: 2.2rem;
  button {
    width: 4.5rem; height: 1.6rem; padding: 0; border: 0; background: none; cursor: pointer; position: relative;
    &::before { content: ''; position: absolute; left: 0; right: 0; top: 50%; height: 5px; margin-top: -2.5px; background: rgba(255,255,255,.35); border-radius: 3px; box-shadow: 0 1px 6px rgba(0,0,0,.35); }
    i { position: absolute; left: 0; right: 0; top: 50%; height: 5px; margin-top: -2.5px; background: var(--acento); border-radius: 3px; transform-origin: left; transform: scaleX(0); }
    &:hover::before { background: rgba(255,255,255,.55); }
    &:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
    &[aria-current='true'] i { animation: ${llenar} var(--dur) linear both; animation-play-state: var(--estado, running); }
    &.visto i { transform: scaleX(1); }
  }
  @media (prefers-reduced-motion: reduce) { button[aria-current='true'] i { animation: none; transform: scaleX(1); } }
  @media (max-width: 700px) { justify-content: center; margin-top: 1.4rem; }
`;
const Lateral = styled.div`
  position: absolute; right: 0; bottom: calc(9vw + 6.4rem); z-index: 2;
  display: flex; align-items: center; gap: .9rem;
  @media (max-width: 700px) { display: none; }
`;
const Aval = styled.span`
  background: rgba(20,24,32,.6); border-left: 3px solid var(--acento);
  padding: .5rem 4vw .5rem .9rem; font-size: 1rem; font-weight: 500;
`;

// ---------- Filas ----------
const Filas = styled.div`
  position: relative; z-index: 3;
  margin-top: -9vw;
  display: flex; flex-direction: column; gap: clamp(1.8rem, 3vw, 3rem);
  scroll-margin-top: 5.5rem;              /* bajo el header fijo al ir a «Ver todos» */
  @media (max-width: 700px) { margin-top: 0; }
`;
// Título de fila: nombre + etiqueta opcional («Habilidad»); al posar el cursor en la fila
// aparece «Ver todos ›» (solo si no caben todos). Novedades lleva un punto que late.
const FilaCabecera = styled.div`
  display: flex; align-items: flex-end; justify-content: space-between; gap: 1rem;
  padding: 0 var(--lateral); margin-bottom: .7rem;
  .izq { display: flex; align-items: baseline; gap: 1rem; min-width: 0; }
  h2 {
    margin: 0; display: inline-flex; align-items: baseline; gap: .5rem;
    font-size: clamp(1.15rem, 1.6vw, 1.55rem); font-weight: 700; letter-spacing: -.005em;
  }
  /* «Habilidad»: medidor de nivel (tres barras que suben y bajan) dentro de una
     píldora con un borde de luz del acento que gira. Al pasar por la fila, las
     barras se llenan y la píldora se tiñe del acento. */
  .etiqueta {
    position: relative; isolation: isolate; overflow: hidden; align-self: center;
    display: inline-flex; align-items: center; gap: .42rem;
    padding: .26rem .66rem .28rem .56rem; border-radius: 999px;
    font-size: .74rem; font-weight: 700; letter-spacing: .01em; line-height: 1.1; color: #fff;
    box-shadow: 0 0 0 0 transparent;
    transition: box-shadow .3s ease;
  }
  .etiqueta::before {                      /* luz que gira detrás del borde */
    content: ''; position: absolute; z-index: -2; left: 50%; top: 50%;
    width: 180px; height: 180px; margin: -90px 0 0 -90px;
    background: conic-gradient(from 0deg, transparent 0 62%, color-mix(in srgb, var(--acento) 70%, transparent) 80%, #fff 88%, transparent 96%);
    animation: ${girarLuz} 3.6s linear infinite;
  }
  .etiqueta::after {                       /* relleno: deja ver 1 px de la luz como borde */
    content: ''; position: absolute; z-index: -1; inset: 1px; border-radius: inherit;
    background: color-mix(in srgb, var(--fondo) 88%, #fff 5%);
    box-shadow: inset 0 0 0 1px rgba(255,255,255,.1);
    transition: background .3s ease;
  }
  .nivel { display: inline-flex; align-items: flex-end; gap: 2px; height: 11px; }
  .nivel b {
    display: block; width: 3px; height: 100%; border-radius: 1.5px; background: var(--acento);
    transform-origin: bottom; animation: ${ecualizar} 1.1s ease-in-out infinite alternate;
    transition: transform .3s ease;
  }
  .nivel b:nth-child(1) { animation-delay: -.45s; }
  .nivel b:nth-child(2) { animation-delay: -.1s; animation-duration: .9s; }
  .nivel b:nth-child(3) { animation-delay: -.7s; animation-duration: 1.3s; }
  section:hover & .etiqueta { box-shadow: 0 0 18px -4px color-mix(in srgb, var(--acento) 70%, transparent); }
  section:hover & .etiqueta::after { background: color-mix(in srgb, var(--acento) 34%, var(--fondo)); }
  section:hover & .nivel b { animation-play-state: paused; transform: scaleY(1); background: #fff; }
  @media (prefers-reduced-motion: reduce) {
    .etiqueta::before { animation: none; opacity: .5; }
    .nivel b { animation: none; }
  }
  .pulso { position: relative; align-self: center; flex: none; width: .5rem; height: .5rem; border-radius: 50%; background: var(--acento); margin-right: .1rem; }
  .pulso::after {
    content: ''; position: absolute; inset: 0; border-radius: 50%; background: var(--acento);
    animation: ${latido} 1.9s cubic-bezier(.2,.6,.3,1) infinite;
  }
  /* «Ver todos»: el mismo lenguaje que «Habilidad» (píldora con borde de luz que
     gira) y la flecha que avanza sin parar en su ventanita. Aparece al pasar por
     la fila; al pasar por el botón se llena del acento y la flecha se acelera. */
  .todos {
    position: relative; isolation: isolate; overflow: hidden; align-self: center;
    display: inline-flex; align-items: center; gap: .35rem;
    padding: .3rem .5rem .32rem .78rem; border: 0; border-radius: 999px; background: none; cursor: pointer;
    font: inherit; font-size: .8rem; font-weight: 700; letter-spacing: .01em; color: #fff; white-space: nowrap;
    opacity: 0; transform: translateX(-.7rem);
    transition: opacity .25s ease, transform .35s cubic-bezier(.2,.8,.2,1), box-shadow .3s ease;
  }
  .todos::before {
    content: ''; position: absolute; z-index: -2; left: 50%; top: 50%;
    width: 180px; height: 180px; margin: -90px 0 0 -90px;
    background: conic-gradient(from 180deg, transparent 0 62%, color-mix(in srgb, var(--acento) 70%, transparent) 80%, #fff 88%, transparent 96%);
    animation: ${girarLuz} 3.6s linear infinite;
  }
  .todos::after {
    content: ''; position: absolute; z-index: -1; inset: 1px; border-radius: inherit;
    background: color-mix(in srgb, var(--fondo) 88%, #fff 5%);
    box-shadow: inset 0 0 0 1px rgba(255,255,255,.1);
    transition: background .25s ease;
  }
  .todos .flechas {
    position: relative; display: block; width: 1.05em; height: 1.05em; overflow: hidden; flex: none;
    color: var(--acento); transition: color .25s ease;
  }
  .todos .flechas span {
    position: absolute; inset: 0 auto 0 0; display: flex; width: 200%;
    animation: ${avanzar} 1.3s cubic-bezier(.5,0,.3,1) infinite;
  }
  .todos .flechas svg { width: 50%; height: 100%; flex: none; }
  .todos:hover { box-shadow: 0 0 18px -4px color-mix(in srgb, var(--acento) 75%, transparent); }
  .todos:hover::after { background: var(--acento); }
  .todos:hover .flechas { color: #fff; }
  .todos:hover .flechas span { animation-duration: .6s; }
  .todos:focus-visible { opacity: 1; transform: none; outline: 2px solid var(--acento); outline-offset: 3px; }
  section:hover & .todos, section:focus-within & .todos { opacity: 1; transform: none; }
  @media (hover: none) { .todos { opacity: 1; transform: none; } }
  @media (prefers-reduced-motion: reduce) {
    .pulso::after { animation: none; }
    .todos { transition: none; }
    .todos::before { animation: none; opacity: .5; }
    .todos .flechas span { animation: none; transform: translateX(-50%); }
  }
`;
// Título que agrupa las filas que le siguen: más grande que el de una fila y
// con una raya hasta el borde que marca dónde empieza el grupo.
const TituloGrupo = styled.h2`
  display: flex; align-items: center; gap: 1.1rem;
  margin: clamp(.6rem, 1.5vw, 1.4rem) 0 calc(-1 * clamp(.6rem, 1.4vw, 1.4rem));
  padding: 0 var(--lateral);
  font-size: clamp(1.5rem, 2.4vw, 2.3rem); font-weight: 800; letter-spacing: -.015em; line-height: 1.1;
  &::after { content: ''; flex: 1; height: 1px; background: linear-gradient(to right, rgba(255,255,255,.35), transparent); }
`;
const Paginas = styled.div`
  display: flex; gap: 3px;
  span { width: .9rem; height: 2px; background: rgba(255,255,255,.28); transition: background .2s; }
  span[data-on='1'] { background: rgba(255,255,255,.9); }
  @media (max-width: 700px) { display: none; }
`;
const Carril = styled.div`
  position: relative;
  &:hover > button { background: rgba(5,7,11,.62); }
`;
const Pista = styled.div`
  --gap: .45vw;
  --por-fila: 5;                 /* como máximo 5 miniaturas a la vista en escritorio */
  display: flex; gap: var(--gap);
  overflow-x: auto; overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory; scroll-padding-inline: var(--lateral);
  padding: 0 var(--lateral);
  scrollbar-width: none; &::-webkit-scrollbar { display: none; }
  @media (max-width: 1100px) { --por-fila: 4; }
  @media (max-width: 800px) { --por-fila: 3; }
  @media (max-width: 560px) { --por-fila: 2.25; --gap: 2vw; }
  ${p => p.$top && css`
    --por-fila: 5;
    @media (max-width: 1400px) { --por-fila: 4.4; }
    @media (max-width: 1100px) { --por-fila: 3.4; }
    @media (max-width: 800px) { --por-fila: 2.6; }
    @media (max-width: 560px) { --por-fila: 1.7; }
  `}
  > * { flex: 0 0 calc((100% - (var(--por-fila) - 1) * var(--gap)) / var(--por-fila)); scroll-snap-align: start; }
  /* La que está bajo el cursor pasa adelante y el resto de la fila se apaga. */
  @media (hover: hover) and (pointer: fine) {
    > * { transition: filter .35s ease, opacity .35s ease; }
    &:hover > * { filter: brightness(.5) saturate(.75); }
    &:hover > *:hover { filter: none; }
  }
  ${p => p.$top && css`
    padding-block: 2.2rem; margin-block: -2.2rem;
  `}
`;
const Flecha = styled.button`
  position: absolute; top: 0; bottom: 0; ${p => p.$lado}: 0; z-index: 4;
  width: var(--lateral); min-width: 2.2rem; border: 0; cursor: pointer; color: #fff;
  /* Visible siempre que haya más cursos hacia ese lado (si no, lleva hidden). */
  background: ${p => `linear-gradient(to ${p.$lado === 'left' ? 'right' : 'left'}, rgba(5,7,11,.7), rgba(5,7,11,.15))`};
  transition: background .2s;
  display: grid; place-items: center;
  svg { width: 2rem; height: 2rem; transition: transform .15s; }
  &:hover { background: rgba(5,7,11,.72); svg { transform: scale(1.25); } }
  &:focus-visible { outline: 2px solid var(--acento); outline-offset: -2px; }
  &[hidden] { display: none; }
  @media (hover: none) { display: none; }
`;
const Miniatura = styled.button`
  position: relative; display: block; padding: 0; border: 0; cursor: pointer; text-align: left;
  aspect-ratio: ${p => p.$vertical ? '2 / 3' : '16 / 9'};
  border-radius: 5px; overflow: hidden; background: var(--superficie); color: #fff;
  container-type: inline-size;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;
const PosterGenerado = styled.div`
  position: absolute; inset: 0; padding: 9% 9% 8%; overflow: hidden;
  transition: padding-bottom .45s cubic-bezier(.2,.8,.2,1);
  > svg {
    position: absolute; pointer-events: none; fill: none; stroke: rgba(255,255,255,.17);
    stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round;
    width: ${p => p.$vertical ? '96%' : '58%'}; right: ${p => p.$vertical ? '-26%' : '-8%'}; top: ${p => p.$vertical ? '6%' : '-10%'};
  }
  > span, > strong { position: relative; }
  display: flex; flex-direction: column; justify-content: flex-end;
  background:
    radial-gradient(120% 90% at 100% 0%, color-mix(in srgb, ${p => p.$tono} 70%, #fff 18%) 0%, transparent 55%),
    linear-gradient(160deg, ${p => p.$tono} 0%, color-mix(in srgb, ${p => p.$tono} 45%, #000) 100%);
  &::before {
    content: ''; position: absolute; left: 9%; top: 11%; width: 14%; height: 3px; background: var(--acento);
  }
  span { font-size: ${p => p.$vertical ? '7.5cqw' : '5.2cqw'}; color: rgba(255,255,255,.75); margin-bottom: .35em; font-weight: 500; }
  strong {
    font-weight: 750; font-stretch: 78%; line-height: .98; letter-spacing: .005em;
    font-size: ${p => p.$vertical ? '13cqw' : '9.6cqw'};
    overflow-wrap: anywhere; hyphens: auto;
    display: -webkit-box; -webkit-line-clamp: ${p => p.$vertical ? 5 : 3}; -webkit-box-orient: vertical; overflow: hidden;
  }
`;
// Título sobre una portada con foto, como en las plataformas de streaming. La
// posición cambia entre cursos (o la fija el admin con `posicion_titulo`) y el
// velo oscuro sigue al texto para que siempre se lea.
const POSICIONES_TITULO = ['abajo', 'arriba', 'abajo-der', 'centro'];
const posicionTitulo = (it) => {
  if (it.posicion_titulo) return it.posicion_titulo;
  const k = String(it.key || it.titulo || '');
  let h = 0;
  for (let i = 0; i < k.length; i += 1) h = (h * 31 + k.charCodeAt(i)) >>> 0;
  return POSICIONES_TITULO[h % POSICIONES_TITULO.length];
};
const VELOS = {
  abajo: 'linear-gradient(to top, rgba(4,7,12,.9) 0%, rgba(4,7,12,.55) 38%, transparent 70%)',
  'abajo-der': 'linear-gradient(to top left, rgba(4,7,12,.9) 0%, rgba(4,7,12,.5) 40%, transparent 72%)',
  arriba: 'linear-gradient(to bottom, rgba(4,7,12,.88) 0%, rgba(4,7,12,.5) 38%, transparent 70%)',
  centro: 'radial-gradient(90% 75% at 50% 50%, rgba(4,7,12,.72) 0%, rgba(4,7,12,.35) 60%, rgba(4,7,12,.15) 100%)',
};
const TituloFoto = styled.div`
  position: absolute; inset: 0; z-index: 1; pointer-events: none;
  display: flex; flex-direction: column; padding: 8% 8% 7%;
  background: ${p => VELOS[p.$pos] || VELOS.abajo};
  justify-content: ${p => (p.$pos === 'arriba' ? 'flex-start' : p.$pos === 'centro' ? 'center' : 'flex-end')};
  align-items: ${p => (p.$pos === 'abajo-der' ? 'flex-end' : p.$pos === 'centro' ? 'center' : 'flex-start')};
  text-align: ${p => (p.$pos === 'abajo-der' ? 'right' : p.$pos === 'centro' ? 'center' : 'left')};
  ${p => p.$pos === 'arriba' ? 'padding-top: 12%;' : ''}
  span {
    font-size: ${p => p.$vertical ? '7cqw' : '4.8cqw'}; font-weight: 500; color: rgba(255,255,255,.8);
    margin-bottom: .3em; text-shadow: 0 1px 6px rgba(0,0,0,.6);
  }
  strong {
    max-width: ${p => (p.$pos === 'centro' ? '92%' : '86%')};
    font-weight: 750; font-stretch: 78%; line-height: .98; letter-spacing: .005em; color: #fff;
    font-size: ${p => p.$vertical ? '12cqw' : '8.6cqw'};
    text-shadow: 0 2px 14px rgba(0,0,0,.55);
    overflow-wrap: anywhere; hyphens: auto;
    display: -webkit-box; -webkit-line-clamp: ${p => p.$vertical ? 5 : 3}; -webkit-box-orient: vertical; overflow: hidden;
  }
`;
const Cinta = styled.span`
  position: absolute; top: 0; left: 50%; transform: translateX(-50%); z-index: 2;
  background: var(--acento); color: var(--sobre-acento, #04201d); font-size: .7rem; font-weight: 700;
  padding: .18rem .55rem .22rem; border-radius: 0 0 4px 4px; white-space: nowrap;
`;
// Puesto en «Novedades» (1 = el último curso subido): una muesca oscura pegada
// a la esquina, con la cifra en la letra condensada de los títulos y una raya
// del acento debajo, como el número de un capítulo.
const Numero = styled.span`
  position: absolute; top: 0; left: 0; z-index: 2; pointer-events: none;
  display: flex; flex-direction: column; align-items: center; gap: 1.6cqw;
  min-width: 15cqw; padding: 2.8cqw 3.4cqw 2.6cqw;
  background: rgba(5,7,11,.74); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  border-radius: 5px 0 7px 0;
  font-weight: 800; font-stretch: 75%; font-size: 13cqw; line-height: .78; letter-spacing: -.04em;
  font-variant-numeric: tabular-nums; color: #fff;
  &::after { content: ''; width: 64%; height: max(2px, .9cqw); border-radius: 1px; background: var(--acento); }
`;
// Avance en la miniatura: pegado al borde inferior, de lado a lado, con brillo.
const Avance = styled.div`
  position: absolute; left: 0; right: 0; bottom: 0; height: 4px; z-index: 2;
  background: rgba(255,255,255,.2);
  transition: height .2s ease;
  i {
    display: block; height: 100%; width: ${p => p.$pct}%;
    background: linear-gradient(90deg, color-mix(in srgb, var(--acento) 65%, #000), var(--acento) 70%, color-mix(in srgb, var(--acento) 70%, #fff));
    box-shadow: 0 0 10px color-mix(in srgb, var(--acento) 80%, transparent);
    border-radius: 0 2px 2px 0;
  }
  button:hover > & { height: 6px; }
`;

// Barra de lecciones: un segmento por lección (hasta 24); con más, continua.
// Las vistas brillan y la siguiente pendiente late suave.
const latirSegmento = keyframes`0%, 100% { opacity: .35; } 50% { opacity: .9; }`;
const Segmentos = styled.span`
  display: flex; gap: 3px; width: 100%; height: ${p => p.$alto || 6}px;
  > i { flex: 1; border-radius: 3px; background: rgba(255,255,255,.16); }
  > i.hecha {
    background: linear-gradient(180deg, color-mix(in srgb, var(--acento) 80%, #fff), var(--acento));
    box-shadow: 0 0 8px color-mix(in srgb, var(--acento) 60%, transparent);
  }
  > i.sigue { background: color-mix(in srgb, var(--acento) 45%, transparent); animation: ${latirSegmento} 1.8s ease-in-out infinite; }
  > b {
    flex: 1; position: relative; border-radius: 999px; background: rgba(255,255,255,.16); overflow: hidden;
  }
  > b::after {
    content: ''; position: absolute; inset: 0 auto 0 0; width: var(--pct); border-radius: 999px;
    background: linear-gradient(90deg, color-mix(in srgb, var(--acento) 65%, #000), var(--acento) 70%, color-mix(in srgb, var(--acento) 70%, #fff));
    box-shadow: 0 0 10px color-mix(in srgb, var(--acento) 70%, transparent);
  }
  @media (prefers-reduced-motion: reduce) { > i.sigue { animation: none; opacity: .6; } }
`;
const BarraLecciones = ({ hechas = 0, total = 0, pct = 0, alto, siguiente = true }) => (
  total > 0 && total <= 24 ? (
    <Segmentos $alto={alto} role="img" aria-label={`${hechas} de ${total} lecciones`}>
      {Array.from({ length: total }, (_, k) => <i key={k} className={k < hechas ? 'hecha' : (siguiente && k === hechas) ? 'sigue' : ''} />)}
    </Segmentos>
  ) : (
    <Segmentos $alto={alto} role="img" aria-label={`${Math.round(pct)} %`}>
      <b style={{ '--pct': `${Math.min(100, Math.max(0, pct))}%` }} />
    </Segmentos>
  )
);
// % sobre la miniatura (modo progreso), junto a la barra de avance.
const PctMini = styled.span`
  position: absolute; right: 6px; top: 6px; z-index: 3;
  padding: .16rem .45rem; border-radius: 5px; background: rgba(5,7,11,.78);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.14);
  font-size: .7rem; font-weight: 700; color: #fff; font-variant-numeric: tabular-nums;
`;

// Anillo de avance (botón «Mi progreso» y panel).
const Anillo = ({ pct, tam = 30, grosor = 3.5 }) => {
  const r = (tam - grosor) / 2; const c = 2 * Math.PI * r;
  return (
    <svg width={tam} height={tam} viewBox={`0 0 ${tam} ${tam}`} aria-hidden="true" style={{ flex: 'none', transform: 'rotate(-90deg)' }}>
      <circle cx={tam / 2} cy={tam / 2} r={r} fill="none" stroke="rgba(255,255,255,.18)" strokeWidth={grosor} />
      <circle cx={tam / 2} cy={tam / 2} r={r} fill="none" stroke="var(--acento)" strokeWidth={grosor} strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(100, Math.max(0, pct)) / 100)} style={{ transition: 'stroke-dashoffset .8s cubic-bezier(.2,.8,.2,1)' }} />
    </svg>
  );
};

// «Mi progreso»: píldora con el anillo del avance total; abre el panel lateral.
const BotonProgreso = styled.button`
  display: inline-flex; align-items: center; gap: .7rem; cursor: pointer; border: 0;
  height: 3.1rem; padding: 0 1.2rem 0 .45rem; border-radius: 999px;
  background: rgba(8,10,14,.55); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.22);
  color: #fff; font: inherit; font-size: 1rem; font-weight: 700; text-align: left;
  transition: background .2s, box-shadow .2s, transform .15s;
  .anillo { position: relative; display: grid; place-items: center; width: 2.3rem; height: 2.3rem; border-radius: 50%; background: rgba(0,0,0,.35); }
  .anillo b { position: absolute; font-size: .62rem; font-weight: 800; font-variant-numeric: tabular-nums; }
  small { display: block; font-size: .72rem; font-weight: 500; color: rgba(255,255,255,.72); }
  &:hover { background: rgba(8,10,14,.75); box-shadow: inset 0 0 0 1px rgba(255,255,255,.5); transform: translateY(-1px); }
  &:focus-visible { outline: 2px solid var(--acento); outline-offset: 3px; }
  @media (max-width: 700px) { height: 2.7rem; font-size: .9rem; }
`;

const entrarPanel = keyframes`from { transform: translateX(100%); } to { transform: none; }`;
const VeloPanel = styled.div`
  position: fixed; inset: 0; z-index: 2600; background: rgba(0,0,0,.55);
  backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
`;
const Panel = styled.aside`
  position: absolute; top: 0; right: 0; bottom: 0; width: min(460px, 100%); overflow-y: auto;
  background: color-mix(in srgb, var(--fondo) 92%, #fff 3%); color: var(--texto);
  box-shadow: -30px 0 80px -20px rgba(0,0,0,.8);
  animation: ${entrarPanel} .35s cubic-bezier(.2,.8,.2,1);
  @media (prefers-reduced-motion: reduce) { animation: none; }
  .cab { position: relative; padding: 2rem 1.6rem 1.4rem; display: flex; gap: 1.2rem; align-items: center;
    background: linear-gradient(160deg, color-mix(in srgb, var(--acento) 26%, transparent), transparent 70%); }
  .grande { position: relative; display: grid; place-items: center; }
  .grande b { position: absolute; font-size: 1.35rem; font-weight: 800; font-stretch: 80%; }
  h2 { margin: 0 0 .35rem; font-size: 1.45rem; font-weight: 800; font-stretch: 85%; }
  .stats { display: flex; flex-wrap: wrap; gap: .35rem 1rem; font-size: .85rem; color: var(--tenue); }
  .stats strong { color: var(--texto); }
  h3 { margin: 1.5rem 1.6rem .6rem; font-size: .95rem; font-weight: 700; color: var(--tenue); }
  .vacio { margin: 0 1.6rem; font-size: .9rem; color: var(--tenue); }
`;
const CerrarPanel = styled.button`
  position: absolute; top: .9rem; right: .9rem; width: 2.2rem; height: 2.2rem; border-radius: 50%; border: 0; cursor: pointer;
  display: grid; place-items: center; background: rgba(255,255,255,.1); color: #fff;
  svg { width: 1rem; height: 1rem; }
  &:hover { background: rgba(255,255,255,.2); }
  &:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
`;
const FilaCurso = styled.div`
  display: grid; grid-template-columns: 92px minmax(0, 1fr); gap: .9rem; align-items: center;
  margin: 0 1rem; padding: .6rem; border-radius: 10px;
  &:hover { background: rgba(255,255,255,.05); }
  .foto { aspect-ratio: 16 / 9; border-radius: 6px; overflow: hidden; background: var(--superficie); }
  .foto img { width: 100%; height: 100%; object-fit: cover; display: block; }
  strong { display: block; font-size: .92rem; font-weight: 650; line-height: 1.25; }
  .barra { display: block; margin: .5rem 0 .35rem; }
  .pie { display: flex; justify-content: space-between; align-items: center; gap: .6rem; font-size: .76rem; color: var(--tenue); }
  button { border: 0; cursor: pointer; border-radius: 6px; padding: .3rem .7rem; font: inherit; font-size: .76rem; font-weight: 700;
    background: #fff; color: #06080c; white-space: nowrap; }
  button:hover { background: var(--acento); color: var(--sobre-acento); }
  button:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
`;

// Franja con precio o avance que sube sobre la portada en el Top 10.
const Revelar = styled.span`
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 4;
  display: flex; justify-content: space-between; align-items: center; gap: .5rem;
  padding: 1.6rem 8% 7%; font-size: max(.72rem, 6.4cqw); font-weight: 600;
  background: linear-gradient(to top, rgba(4,7,12,.92), transparent);
  transform: translateY(100%); opacity: 0; transition: transform .45s cubic-bezier(.2,.8,.2,1), opacity .3s;
  em { font-style: normal; color: var(--acento); }
`;
const TopItem = styled.div`
  display: flex; align-items: flex-end; position: relative; container-type: inline-size;
  > b {
    flex: 0 0 44%; margin-right: -2%; align-self: stretch;
    display: flex; align-items: flex-end; justify-content: flex-end; overflow: visible;
    font-weight: 800; font-stretch: 75%; line-height: .74; letter-spacing: -.07em;
    font-size: ${p => p.$doble ? '52cqw' : '84cqw'};
    color: var(--fondo); -webkit-text-stroke: max(2px, .8cqw) color-mix(in srgb, var(--texto) 48%, transparent);
    paint-order: stroke fill; user-select: none; white-space: nowrap;
  }
  > button {
    flex: 0 0 58%; z-index: 1; overflow: hidden;
    transform: perspective(900px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg));
    transition: transform .55s cubic-bezier(.2,.8,.2,1), box-shadow .55s ease;
  }
  /* Brillo que sigue al cursor sobre la portada. */
  > button::after {
    content: ''; position: absolute; inset: 0; z-index: 3; pointer-events: none; opacity: 0; transition: opacity .35s;
    background: radial-gradient(circle at var(--mx, 50%) var(--my, 30%), rgba(255,255,255,.3), transparent 48%);
  }
  > b { transition: color .45s ease, -webkit-text-stroke-color .45s ease, transform .6s cubic-bezier(.2,.8,.2,1), text-shadow .45s ease; transform-origin: 100% 100%; }
  @media (hover: hover) and (pointer: fine) {
    &:hover { z-index: 5; }
    &:hover > b {
      color: color-mix(in srgb, var(--acento) 16%, var(--fondo));
      -webkit-text-stroke-color: var(--acento);
      text-shadow: 0 0 34px color-mix(in srgb, var(--acento) 55%, transparent);
      transform: translateX(-7%) scale(1.06);
    }
    &:hover > button {
      transform: perspective(900px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) scale(1.08);
      box-shadow: 0 26px 50px rgba(0,0,0,.65), 0 0 0 1px color-mix(in srgb, var(--acento) 55%, transparent);
    }
    &:hover > button::after { opacity: 1; }
    &:hover > button > div { padding-bottom: 34%; }
    &:hover ${Revelar} { transform: none; opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    > button, > b { transition: none; transform: none !important; }
  }
`;

// ---------- Especialidades ----------
const Chips = styled.div`
  display: flex; gap: .5rem; padding: 0 var(--lateral); overflow-x: auto; scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  button {
    flex: none; border-radius: 999px; padding: .5rem 1.05rem; font-size: .95rem; font-weight: 500; cursor: pointer;
    background: rgba(8,13,22,.55); color: var(--texto); border: 1px solid rgba(255,255,255,.22); backdrop-filter: blur(8px);
    transition: background .2s, color .2s, border-color .2s;
    &:hover { border-color: rgba(255,255,255,.6); }
    &[aria-pressed='true'] { background: var(--texto); color: var(--fondo); border-color: var(--texto); }
  }
`;
const Rejilla = styled.div`
  display: grid; gap: 1.6rem .6rem; padding: 0 var(--lateral);
  grid-template-columns: repeat(auto-fill, minmax(min(260px, 44vw), 1fr));
`;
const TituloFiltro = styled.div`
  display: flex; align-items: baseline; gap: 1rem; flex-wrap: wrap; padding: 0 var(--lateral);
  h2 { margin: 0; font-size: clamp(1.6rem, 3vw, 2.4rem); font-weight: 750; font-stretch: 85%; }
  span { color: var(--tenue); }
  button {
    align-self: center; display: inline-flex; align-items: center; gap: .3rem; cursor: pointer;
    padding: .45rem .9rem .45rem .6rem; border-radius: 6px; border: 0; font: inherit; font-size: .9rem; font-weight: 700;
    color: #fff; background: rgba(255,255,255,.1); box-shadow: inset 0 0 0 1px rgba(255,255,255,.26);
    svg { width: 1.1em; height: 1.1em; }
    &:hover { background: rgba(255,255,255,.18); }
    &:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  }
`;

// ---------- Preguntas y cierre (visitantes) ----------
const Preguntas = styled.section`
  max-width: 62rem; margin: clamp(4rem, 8vw, 7rem) auto 0; padding: 0 var(--lateral);
  h2 { text-align: center; margin: 0 0 1.8rem; font-size: clamp(1.8rem, 3.6vw, 3rem); font-weight: 800; font-stretch: 80%; }
`;
const Pregunta = styled.div`
  margin-bottom: .5rem;
  > button {
    width: 100%; display: flex; justify-content: space-between; align-items: center; gap: 1rem; text-align: left;
    background: color-mix(in srgb, var(--fondo) 70%, #2a3446); color: var(--texto); border: 0; cursor: pointer;
    padding: 1.35rem 1.6rem; font-size: clamp(1.05rem, 1.7vw, 1.4rem); font-weight: 500;
    transition: background .2s;
    &:hover { background: color-mix(in srgb, var(--fondo) 55%, #2a3446); }
    svg { width: 1.8rem; height: 1.8rem; flex: none; transition: transform .35s cubic-bezier(.2,.8,.2,1); }
  }
  &[data-abierta='1'] > button svg { transform: rotate(45deg); }
  > div { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .4s cubic-bezier(.2,.8,.2,1); background: color-mix(in srgb, var(--fondo) 70%, #2a3446); margin-top: 1px; }
  &[data-abierta='1'] > div { grid-template-rows: 1fr; }
  > div > p { overflow: hidden; margin: 0; padding: 0 1.6rem; font-size: clamp(1rem, 1.5vw, 1.2rem); line-height: 1.55; color: color-mix(in srgb, var(--texto) 85%, transparent); }
  &[data-abierta='1'] > div > p { padding: 1.3rem 1.6rem 1.5rem; }
`;
const Cierre = styled.section`
  position: relative; margin: clamp(4rem, 8vw, 6rem) var(--lateral) 0; padding: clamp(2.6rem, 6vw, 4.5rem) clamp(1.4rem, 5vw, 4rem);
  border-radius: 14px; overflow: hidden; text-align: center;
  background:
    radial-gradient(70% 120% at 100% 0%, color-mix(in srgb, var(--acento) 26%, transparent), transparent 60%),
    radial-gradient(60% 120% at 0% 100%, rgba(170,40,70,.28), transparent 60%),
    color-mix(in srgb, var(--fondo) 80%, #1b2433);
  > svg { position: absolute; left: 0; right: 0; bottom: 0; width: 100%; height: 26%; opacity: .2; fill: none; stroke: var(--acento); stroke-width: 2; }
  h2 { position: relative; margin: 0 auto .8rem; max-width: 20ch; font-size: clamp(2rem, 4.6vw, 3.8rem); font-weight: 800; font-stretch: 78%; line-height: .95; text-wrap: balance; }
  p { position: relative; margin: 0 auto 1.8rem; max-width: 40rem; font-size: clamp(1rem, 1.5vw, 1.2rem); color: color-mix(in srgb, var(--texto) 82%, transparent); }
  > button { position: relative; }
`;

// ---------- Vista previa (hover) ----------
const crecer = keyframes`from { opacity: 0; transform: scale(.66); } to { opacity: 1; transform: scale(1); }`;
const Previa = styled.div`
  position: absolute; z-index: 1200; border-radius: 8px; overflow: hidden;
  background: #151b27; color: #fff; font-family: ${FUENTE};
  box-shadow: 0 18px 48px rgba(0,0,0,.65);
  transform-origin: ${p => p.$origen};
  animation: ${crecer} .28s cubic-bezier(.2,.8,.25,1) both;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const PreviaImagen = styled.div`
  position: relative; aspect-ratio: 16 / 9; background: #0c111a; container-type: inline-size;
  img, video { width: 100%; height: 100%; object-fit: cover; display: block; }
`;
const PreviaCuerpo = styled.div`
  padding: 1rem 1.1rem 1.2rem;
  h3 { margin: .7rem 0 .45rem; font-size: 1.12rem; font-weight: 700; line-height: 1.15; }
`;
const FilaBotones = styled.div`
  display: flex; gap: .5rem; align-items: stretch;
`;
// Botones de la vista previa: el mismo lenguaje que los de la cartelera
// (esquinas de 6 px) a escala de tarjeta, repartiéndose el ancho. «Ver curso»
// toma el acento de la marca al posar el cursor; en «Contenido» la flecha baja
// un poco, adelantando que abre el contenido del curso.
const BotonPrevia = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: .45rem; cursor: pointer;
  white-space: nowrap; min-width: 0; height: 2.6rem; padding: 0 .9rem; border-radius: 6px;
  font: inherit; font-size: .95rem; font-weight: 700; letter-spacing: -.005em; line-height: 1;
  transition: background-color .18s ease, color .18s ease, box-shadow .18s ease, transform .12s ease;
  svg { width: 1.1rem; height: 1.1rem; flex: none; transition: transform .2s ease; }
  &:active { transform: scale(.97); }
  &:focus-visible { outline: 2px solid var(--acento, #5ee0d2); outline-offset: 2px; }
  ${p => p.$lleno ? css`
    flex: 1.35 1 0; border: 0; background: #fff; color: #05070b;
    &:hover { background: var(--acento, #5ee0d2); color: var(--sobre-acento, #fff); }
  ` : css`
    flex: 1 1 0; border: 0; color: #fff;
    background: rgba(255,255,255,.1); box-shadow: inset 0 0 0 1px rgba(255,255,255,.26);
    backdrop-filter: blur(6px);
    &:hover { background: rgba(255,255,255,.18); box-shadow: inset 0 0 0 1px rgba(255,255,255,.55); }
    &:hover svg { transform: translateY(2px); }
  `}
  @media (prefers-reduced-motion: reduce) { transition: none; svg { transition: none; } &:active { transform: none; } &:hover svg { transform: none; } }
`;
const Datos = styled.div`
  display: flex; flex-wrap: wrap; gap: .3rem .8rem; align-items: center; font-size: .86rem; color: #b8c1cf;
  em { font-style: normal; color: var(--acento, #5ee0d2); font-weight: 700; }
  s { opacity: .6; }
`;

// ---------- Ficha (modal) ----------
const aparecer = keyframes`from { opacity: 0; transform: translate3d(0, 28px, 0) scale(.98); } to { opacity: 1; transform: none; }`;
const Velo = styled.div`
  position: fixed; inset: 0; z-index: 1300; background: rgba(0,0,0,.72);
  overflow-y: auto; padding: 2rem 1rem; font-family: ${FUENTE};
  @media (max-width: 700px) { padding: 0; }
`;
const Ficha = styled.div`
  position: relative; margin: 0 auto; width: min(900px, 100%);
  background: #121824; color: #f2f5f9; border-radius: 10px; overflow: hidden;
  box-shadow: 0 30px 80px rgba(0,0,0,.6);
  animation: ${aparecer} .35s cubic-bezier(.2,.8,.25,1) both;
  @media (prefers-reduced-motion: reduce) { animation: none; }
  @media (max-width: 700px) { border-radius: 0; min-height: 100vh; }
`;
const FichaPortada = styled.div`
  position: relative; aspect-ratio: 16 / 8.2; container-type: inline-size;
  img, video { width: 100%; height: 100%; object-fit: cover; display: block; }
  &::after { content: ''; position: absolute; inset: 0; background: linear-gradient(to top, #121824 4%, transparent 55%); }
  > div.txt { position: absolute; left: 3rem; right: 3rem; bottom: 2rem; z-index: 2; }
  h2 { margin: 0 0 1.1rem; font-weight: 800; font-stretch: 75%; font-size: clamp(2rem, 4.6vw, 3.4rem); line-height: .92; letter-spacing: -.01em; text-wrap: balance; }
  @media (max-width: 700px) {
    aspect-ratio: 4 / 3.4;
    > div.txt { left: 1.2rem; right: 1.2rem; bottom: 1.2rem; }
  }
`;
const Cerrar = styled.button`
  position: absolute; top: 1rem; right: 1rem; z-index: 5; width: 2.4rem; height: 2.4rem; border-radius: 50%;
  border: 0; background: #121824; color: #fff; cursor: pointer; display: grid; place-items: center;
  svg { width: 1.3rem; height: 1.3rem; }
`;
const FichaCuerpo = styled.div`
  padding: .5rem 3rem 3rem;
  display: grid; grid-template-columns: minmax(0, 1fr); gap: 1.5rem 2.5rem;
  p.sinopsis { margin: .8rem 0 0; font-size: 1.05rem; line-height: 1.55; color: #dfe5ee; max-width: 46rem; }
  @media (max-width: 700px) { padding: .5rem 1.2rem 2rem; }
`;
const Precio = styled.div`
  display: flex; align-items: baseline; gap: .7rem; flex-wrap: wrap; margin-top: 1.2rem;
  b { font-size: 1.9rem; font-weight: 800; font-stretch: 85%; }
  s { color: #7d8a9e; }
  span { color: var(--acento, #5ee0d2); font-weight: 600; font-size: .95rem; }
`;
const Temario = styled.div`
  grid-column: 1 / -1;
  h3 { margin: 1rem 0 .3rem; font-size: 1.4rem; font-weight: 700; }
  h4 { margin: 1.4rem 0 .4rem; font-size: 1rem; font-weight: 600; color: #aeb8c7; display: flex; justify-content: space-between; gap: 1rem; }
  ol { list-style: none; margin: 0; padding: 0; }
  li {
    display: grid; grid-template-columns: 2.6rem 1fr; align-items: center; gap: .8rem;
    padding: 1.05rem .9rem; border-bottom: 1px solid #263041; font-size: 1rem;
  }
  li:first-child { border-top: 1px solid #263041; }
  /* Fila directa (módulo de una sola lección): mismo peso que un título de módulo, entre los bloques. */
  ol.directo li:first-child { border-top: 0; }
  h3 + div > ol.directo li:first-child, h4 + ol + div > ol.directo li:first-child { border-top: 1px solid #263041; }
  ol.directo li.enlace > button span { font-weight: 600; }
  li b { font-size: 1.4rem; font-weight: 500; color: #8795a9; text-align: center; }
  /* Cada lección (y el título del módulo) lleva a esa parte del curso. */
  li.enlace { padding: 0; }
  li.enlace > button {
    grid-column: 1 / -1; display: grid; grid-template-columns: 2.6rem 1fr auto; align-items: center; gap: .8rem;
    width: 100%; padding: 1.05rem .9rem; border: 0; background: none; color: inherit; font: inherit; text-align: left; cursor: pointer;
    border-radius: 6px; transition: background .15s;
  }
  li.enlace > button:hover, li.enlace > button:focus-visible { background: rgba(255,255,255,.06); outline: none; }
  li.enlace > button:focus-visible { box-shadow: inset 0 0 0 2px var(--acento); }
  li.enlace i { display: grid; place-items: center; width: 2rem; height: 2rem; border-radius: 50%; color: #fff; opacity: .35; transition: opacity .15s, background .15s; }
  li.enlace i svg { width: .9rem; height: .9rem; margin-left: 2px; }
  li.enlace > button:hover i, li.enlace > button:focus-visible i { opacity: 1; background: var(--acento); color: var(--sobre-acento, #fff); }
  /* Examen dentro del temario: mismo ritmo que una lección, con su ícono y el % para aprobar. */
  li.examen b { display: grid; place-items: center; color: var(--acento); }
  li.examen b svg { width: 1.35rem; height: 1.35rem; }
  li.examen span small { display: block; margin-top: .15rem; color: #8795a9; font-size: .85rem; }
  h4.final { color: #fff; }
  h4 button { padding: 0; border: 0; background: none; color: inherit; font: inherit; cursor: pointer; text-align: left; }
  h4 button:hover, h4 button:focus-visible { color: #fff; text-decoration: underline; text-decoration-color: var(--acento); text-underline-offset: 4px; outline: none; }
  .avance-mod { display: inline-flex; align-items: center; gap: .6rem; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .avance-mod i { display: block; width: 4.5rem; height: 4px; border-radius: 2px; background: rgba(255,255,255,.16); overflow: hidden; }
  .avance-mod b { display: block; height: 100%; border-radius: 2px; background: var(--acento); box-shadow: 0 0 8px color-mix(in srgb, var(--acento) 60%, transparent); }
`;

// Ficha: «Sobre el curso» a la izquierda, «Detalles» y docente a la derecha.
const FichaInfo = styled.div`
  grid-column: 1 / -1;
  display: grid; grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr); gap: 1.6rem 2.6rem; align-items: start;
  @media (max-width: 760px) { grid-template-columns: 1fr; }
  h3 { margin: .2rem 0 .7rem; font-size: 1.25rem; font-weight: 700; }
`;
const Descripcion = styled.div`
  color: #cfd6e0; font-size: 1rem; line-height: 1.6;
  p { margin: 0 0 .75rem; }
  .video { position: relative; aspect-ratio: 16 / 9; max-width: 100%; margin: .4rem 0 .9rem; border-radius: 8px; overflow: hidden; background: #000; }
  .video iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
  p, li { text-align: justify; hyphens: auto; -webkit-hyphens: auto; }
  p.destacado { color: #fff; font-weight: 650; font-size: 1.08rem; }
  h4 { margin: 1.15rem 0 .45rem; color: #fff; font-size: 1.05rem; font-weight: 700; line-height: 1.35; text-wrap: balance; }
  h4:first-child { margin-top: 0; }
  ul { margin: 0 0 .75rem; padding-left: 1.1rem; }
  li { margin: .3rem 0; }
  li::marker { color: var(--acento); }
  &.recortada { max-height: 13.5rem; overflow: hidden; -webkit-mask-image: linear-gradient(#000 70%, transparent); mask-image: linear-gradient(#000 70%, transparent); }
  + button {
    margin-top: .3rem; padding: 0; border: 0; background: none; color: #fff; font: inherit; font-weight: 600;
    cursor: pointer; text-decoration: underline; text-decoration-color: var(--acento); text-underline-offset: 4px;
  }
`;
const Detalles = styled.dl`
  margin: 0; display: grid; gap: .1rem;
  div { display: grid; grid-template-columns: 7.5rem 1fr; gap: .8rem; padding: .62rem 0; border-bottom: 1px solid #263041; }
  dt { color: #8795a9; font-size: .9rem; }
  dd { margin: 0; color: #eef1f6; font-size: .95rem; font-weight: 550; }
`;
const DocenteFicha = styled.div`
  display: flex; gap: .9rem; align-items: center; margin-top: 1.2rem;
  .foto { flex: none; width: 3.4rem; height: 3.4rem; border-radius: 50%; overflow: hidden; background: #263041;
    display: grid; place-items: center; color: #aeb8c7; font-weight: 700; font-size: 1.1rem; box-shadow: 0 0 0 2px var(--acento); }
  .foto img { width: 100%; height: 100%; object-fit: cover; }
  small { display: block; color: #8795a9; font-size: .8rem; }
  b { display: block; color: #fff; font-size: 1rem; font-weight: 650; }
  span { display: block; color: #aeb8c7; font-size: .86rem; }
  /* Perfil del docente (Admin → Docentes → Descripción). */
  p.perfil { margin: .45rem 0 0; color: #aeb8c7; font-size: .86rem; line-height: 1.5; text-align: justify; hyphens: auto; -webkit-hyphens: auto; }
  &.con-perfil { align-items: flex-start; }
`;

// Descripción en párrafos: «## » al inicio es un subtítulo; «• » o «- » arma una lista; la primera línea corta y con «!»/«?» va destacada.
// Video de la ficha (Vimeo o YouTube) → dirección para incrustarlo; '' si no se reconoce.
const embedVideo = (url) => {
  const u = String(url || '').trim();
  let m = u.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-f0-9]+))?(?:[^#]*?[?&]h=([a-f0-9]+))?/i);
  if (m) { const h = m[3] || m[2]; return `https://player.vimeo.com/video/${m[1]}?${h ? `h=${h}&` : ''}title=0&byline=0&portrait=0&dnt=1`; }
  m = u.match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/i);
  return m ? `https://www.youtube-nocookie.com/embed/${m[1]}?rel=0` : '';
};

// Recortada a la altura de la columna de la derecha (portada, avance, detalles y docente), con «Ver más».
const DescripcionCurso = ({ texto, columnaRef, video }) => {   // eslint-disable-line react/prop-types
  const [abierta, setAbierta] = useState(false);
  const caja = useRef(null);
  const [maxAlto, setMaxAlto] = useState(null);   // px; null = el recorte fijo de antes (pantallas angostas)
  const [sobra, setSobra] = useState(false);
  const lineas = String(texto || '').split(/\n+/).map(l => l.trim()).filter(Boolean);
  useLayoutEffect(() => {
    const el = caja.current;
    if (!el) return undefined;
    const medir = () => {
      const der = columnaRef?.current;
      if (!der || window.matchMedia('(max-width: 760px)').matches) {
        setMaxAlto(null); setSobra(String(texto || '').length > 650); return;
      }
      const tope = el.getBoundingClientRect().top - el.parentElement.getBoundingClientRect().top;
      const alto = Math.max(160, der.getBoundingClientRect().height - tope - 13);   // 13: que el «Ver más» cierre con la columna derecha
      setMaxAlto(alto); setSobra(el.scrollHeight > alto + 8);
    };
    // La columna derecha va después en la página: se mide cuando ya está montada (y cada vez que cambia: imágenes, avance).
    let ro = null;
    const cuadro = requestAnimationFrame(() => {
      medir();
      if (typeof ResizeObserver !== 'undefined' && columnaRef?.current) { ro = new ResizeObserver(medir); ro.observe(columnaRef.current); }
    });
    window.addEventListener('resize', medir);
    return () => { cancelAnimationFrame(cuadro); if (ro) ro.disconnect(); window.removeEventListener('resize', medir); };
  }, [columnaRef, texto, video]);
  const embed = embedVideo(video);
  if (!lineas.length && !embed) return null;
  const bloques = [];
  lineas.forEach(l => {
    if (/^#{1,4}\s+/.test(l)) bloques.push({ tipo: 'h', texto: l.replace(/^#{1,4}\s+/, '') });
    else if (/^[•\-–]\s+/.test(l)) {
      const item = l.replace(/^[•\-–]\s+/, '');
      if (bloques.length && bloques[bloques.length - 1].tipo === 'lista') bloques[bloques.length - 1].items.push(item);
      else bloques.push({ tipo: 'lista', items: [item] });
    } else bloques.push({ tipo: 'p', texto: l });
  });
  const larga = sobra;
  return (
    <>
      <Descripcion ref={caja} className={larga && !abierta ? 'recortada' : ''} style={larga && !abierta && maxAlto ? { maxHeight: `${maxAlto}px` } : undefined}>
        {bloques.map((b, i) => (b.tipo === 'h' ? <h4 key={i}>{b.texto}</h4> : b.tipo === 'lista'
          ? <ul key={i}>{b.items.map((t, k) => <li key={k}>{t}</li>)}</ul>
          : <p key={i} className={i === 0 && b.texto.length < 90 && /[!?]$/.test(b.texto) ? 'destacado' : ''}>{b.texto}</p>))}
        {embed && (
          <div className="video">
            <iframe src={embed} title="Video del curso" loading="lazy" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
          </div>
        )}
      </Descripcion>
      {larga && <button type="button" onClick={() => setAbierta(a => !a)}>{abierta ? 'Ver menos' : 'Ver más'}</button>}
    </>
  );
};

const duracionTexto = (seg) => {
  const m = Math.round(seg / 60);
  if (m < 60) return `${m} min de video`;
  const h = Math.floor(m / 60), r = m % 60;
  return `${h} h${r ? ` ${r} min` : ''} de video`;
};

// «Tu avance» en la ficha (con sesión): texto y barra por lecciones.
const TuAvance = styled.div`
  margin: 1.1rem 0 .2rem; padding: .95rem 1.1rem 1.05rem; border-radius: 10px;
  background: rgba(255,255,255,.05); box-shadow: inset 0 0 0 1px rgba(255,255,255,.08);
  .linea { display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; margin-bottom: .6rem; }
  strong { font-size: 1rem; font-weight: 700; color: #fff; }
  span { font-size: .85rem; color: #aab3c0; font-variant-numeric: tabular-nums; }
  /* Encima de «Detalles»: la portada con el título, el avance y la última conexión. */
  &.con-portada { margin: 0 0 1.4rem; padding: 0 0 1rem; overflow: hidden; }
  &.con-portada .linea, &.con-portada > div:not(.foto), &.con-portada small { margin-left: 1rem; margin-right: 1rem; }
  .foto {
    position: relative; aspect-ratio: 16 / 9; max-width: 100%; margin-bottom: .9rem;
    background: #1b2433 center / cover no-repeat;
  }
  .foto::after { content: ''; position: absolute; inset: 0; background: linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,.8) 100%); }
  .foto b {
    position: absolute; left: 1rem; right: 1rem; bottom: .8rem; z-index: 1;
    color: #fff; font-size: 1.05rem; font-weight: 700; line-height: 1.25; text-wrap: balance; text-shadow: 0 1px 3px rgba(0,0,0,.5);
  }
  small { display: block; margin-top: .6rem; font-size: .78rem; color: #8795a9; }
`;

// ============================================================
// ICONOS (inline, heredan color)
// ============================================================
const Ico = {
  play: <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4.5v15a1 1 0 0 0 1.52.85l12-7.5a1 1 0 0 0 0-1.7l-12-7.5A1 1 0 0 0 7 4.5Z" /></svg>,
  info: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="9.5" /><path d="M12 11v6M12 7.2v.1" strokeLinecap="round" /></svg>,
  carrito: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M3 4h2.2l2.3 11.2a1.5 1.5 0 0 0 1.5 1.2h8.6a1.5 1.5 0 0 0 1.5-1.1L21 8H6.2" strokeLinejoin="round" /><circle cx="9.5" cy="20" r="1.3" /><circle cx="17.5" cy="20" r="1.3" /></svg>,
  abajo: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  izq: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="m15 5-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  der: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="m9 5 7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" /></svg>,
  mudo: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" strokeLinejoin="round" /><path d="m16 9.5 5 5M21 9.5l-5 5" strokeLinecap="round" /></svg>,
  examen: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="5" y="3.5" width="14" height="17" rx="2" /><path d="M9 3.5h6v2.5H9z" strokeLinejoin="round" /><path d="m8.8 12.5 2 2 4.4-4.4M9 17.5h6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  sonido: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" strokeLinejoin="round" /><path d="M16 9a4.5 4.5 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11" strokeLinecap="round" /></svg>,
};

// Ilustración de línea para la portada generada, según la especialidad.
const ICONOS_ESP = [
  [/cardio|coraz|hta|hipertens|electrocard/i, 'M32 54S8 40 8 22a12 12 0 0 1 24-4 12 12 0 0 1 24 4c0 18-24 32-24 32Z M6 32h14l4-8 6 16 5-12 3 4h20'],
  [/neuro|cerebr|cefal|acv/i, 'M30 12a9 9 0 0 0-15 6 9 9 0 0 0-5 15 9 9 0 0 0 5 14 9 9 0 0 0 15 5V12Z M34 12a9 9 0 0 1 15 6 9 9 0 0 1 5 15 9 9 0 0 1-5 14 9 9 0 0 1-15 5V12Z M20 26c4 0 6 3 6 6 M44 26c-4 0-6 3-6 6 M19 40c3 0 5 1 7 4 M45 40c-3 0-5 1-7 4'],
  [/neumo|pulm|asma|epoc|respir/i, 'M32 8v22 M32 24c-4 0-6 4-9 5 M32 24c4 0 6 4 9 5 M24 16c-8 0-14 14-14 30 0 6 4 9 8 9 6 0 8-4 8-10V21c0-3-1-5-2-5Z M40 16c8 0 14 14 14 30 0 6-4 9-8 9-6 0-8-4-8-10V21c0-3 1-5 2-5Z'],
  [/endocr|diabet|tiroid|metab/i, 'M32 6S13 30 13 41a19 19 0 0 0 38 0C51 30 32 6 32 6Z M23 43a9 9 0 0 0 9 9'],
  [/infect|antibi|bacter|viru/i, 'M32 18a14 14 0 1 0 0 28 14 14 0 1 0 0-28Z M32 18V7 M32 46v11 M18 32H7 M46 32h11 M22 22l-7-7 M42 42l7 7 M42 22l7-7 M22 42l-7 7 M27 28h.1 M37 36h.1 M35 26h.1'],
  [/urgenc|emergen|trauma|pocus|ecograf/i, 'M24 6h16v18h18v16H40v18H24V40H6V24h18Z'],
  [/derma|piel/i, 'M27 8a19 19 0 1 0 0 38 19 19 0 1 0 0-38Z M41 41l16 16 M19 24a3 3 0 1 0 0 .1 M31 31a5 5 0 1 0 0 .1 M22 36h.1'],
  [/psiq|mental|psico/i, 'M40 57V47c8-2 13-8 13-17A21 21 0 0 0 11 28c0 4 2 7 2 9l-5 8h7v6c0 3 3 6 6 6h6 M28 21c4-4 11-2 11 3 0 5-6 5-6 10'],
];
const ICONO_DEFECTO = 'M16 6v16a12 12 0 0 0 24 0V6 M28 34v6a12 12 0 0 0 24 0v-9 M52 25a5 5 0 1 0 0 .1';
const iconoDe = (esp) => (ICONOS_ESP.find(([re]) => re.test(esp || ''))?.[1] || ICONO_DEFECTO);
const IlustracionEsp = ({ especialidad }) => (
  <svg viewBox="0 0 64 64" aria-hidden="true"><path d={iconoDe(especialidad)} /></svg>
);

// ============================================================
// PIEZAS
// ============================================================
const Portada = ({ item, vertical, conTitulo = false }) => {
  const src = urlImg(vertical ? (item.imagen_vertical || item.imagen) : item.imagen);
  const [roto, setRoto] = useState(false);
  if (src && !roto) {
    const pos = posicionTitulo(item);
    return (
      <>
        <img src={src} alt="" loading="lazy" onError={() => setRoto(true)} />
        {conTitulo && pos !== 'oculto' && (
          <TituloFoto $pos={pos} $vertical={vertical} aria-hidden="true">
            {item.especialidad && <span>{item.especialidad}</span>}
            <strong>{item.titulo_cartelera || item.titulo}</strong>
          </TituloFoto>
        )}
      </>
    );
  }
  return (
    <PosterGenerado $tono={item.color || tonoDe(item.especialidad || item.titulo)} $vertical={vertical}>
      <IlustracionEsp especialidad={`${item.especialidad || ''} ${item.titulo || ''}`} />
      {item.especialidad && <span>{item.especialidad}</span>}
      <strong>{item.titulo_cartelera || item.titulo}</strong>
    </PosterGenerado>
  );
};

const Fila = ({ titulo, top, children, etiqueta = '', enVivo = false, onVerTodos }) => {
  const pista = useRef(null);
  const [pag, setPag] = useState({ n: 1, i: 0, izq: false, der: false });
  const medir = useCallback(() => {
    const el = pista.current;
    if (!el) return;
    const n = Math.max(1, Math.ceil((el.scrollWidth - 2) / el.clientWidth));
    // Al final de la fila se está en la última página aunque esa quede a medias.
    const alFinal = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    const i = alFinal ? n - 1 : Math.min(n - 1, Math.round(el.scrollLeft / el.clientWidth));
    // Las flechas dependen de si queda algo por ver hacia cada lado.
    const izq = el.scrollLeft > 4;
    const der = !alFinal;
    setPag(p => (p.n === n && p.i === i && p.izq === izq && p.der === der ? p : { n, i, izq, der }));
  }, []);
  useEffect(() => {
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [medir, children]);
  const mover = (dir) => {
    const el = pista.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.92, behavior: reduceMovimiento() ? 'auto' : 'smooth' });
  };
  return (
    <section aria-label={titulo}>
      <FilaCabecera>
        <div className="izq">
          <h2>
            {enVivo && <i className="pulso" aria-hidden="true" />}
            {titulo}
            {etiqueta && (
              <span className="etiqueta">
                <i className="nivel" aria-hidden="true"><b /><b /><b /></i>
                {etiqueta}
              </span>
            )}
          </h2>
          {onVerTodos && (pag.izq || pag.der) && (
            <button type="button" className="todos" onClick={onVerTodos}>
              Ver todos
              <i className="flechas" aria-hidden="true"><span>{Ico.der}{Ico.der}</span></i>
            </button>
          )}
        </div>
        {pag.n > 1 && <Paginas aria-hidden="true">{Array.from({ length: pag.n }, (_, k) => <span key={k} data-on={k === pag.i ? 1 : 0} />)}</Paginas>}
      </FilaCabecera>
      <Carril>
        <Flecha type="button" $lado="left" hidden={!pag.izq} onClick={() => mover(-1)} aria-label={`Anteriores en ${titulo}`}>{Ico.izq}</Flecha>
        <Pista ref={pista} $top={top} onScroll={medir}>{children}</Pista>
        <Flecha type="button" $lado="right" hidden={!pag.der} onClick={() => mover(1)} aria-label={`Siguientes en ${titulo}`}>{Ico.der}</Flecha>
      </Carril>
    </section>
  );
};

// ============================================================
// SECCIÓN
// ============================================================
// Avance en el plan de formación, entre la cartelera y «Mis cursos obligatorios».
const PlanAvance = styled.a`
  position: relative; z-index: 2; display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 1.1rem; align-items: center;
  margin: 0 var(--lateral); padding: 1rem 1.2rem; border-radius: 10px; text-decoration: none; color: var(--texto);
  background: var(--superficie); box-shadow: inset 0 0 0 1px rgba(255,255,255,.08); transition: box-shadow .2s, background .2s;
  &:hover { box-shadow: inset 0 0 0 1px rgba(255,255,255,.2); }
  .anillo { position: relative; display: grid; place-items: center; }
  .anillo b { position: absolute; font-size: .95rem; font-weight: 800; font-variant-numeric: tabular-nums; }
  strong { display: block; font-size: 1.05rem; font-weight: 750; color: #fff; }
  span.det { display: block; margin-top: .2rem; font-size: .9rem; color: var(--tenue); }
  span.ir { display: inline-flex; align-items: center; height: 42px; padding: 0 1.1rem; border-radius: 8px; background: rgba(109,109,110,.6); color: #fff; font-weight: 700; font-size: .95rem; white-space: nowrap; }
  @media (max-width: 600px) { grid-template-columns: auto minmax(0, 1fr); span.ir { grid-column: 1 / -1; justify-self: start; } }
`;
// Sin cursos que mostrar (Inactivo, o subrol sin cursos asignados todavía).
const AvisoPlan = styled.div`
  min-height: 70vh; display: grid; place-content: center; text-align: center; gap: .8rem; padding: 9rem var(--lateral) 4rem;
  h2 { margin: 0; font-size: clamp(2rem, 5vw, 3.4rem); font-weight: 800; font-stretch: 78%; line-height: .95; color: #fff; text-wrap: balance; }
  p { margin: 0 auto; max-width: 34rem; font-size: 1.05rem; color: var(--tenue); line-height: 1.55; }
  a { justify-self: center; margin-top: .6rem; display: inline-flex; align-items: center; height: 48px; padding: 0 1.4rem; border-radius: 8px; background: #fff; color: #111; font-weight: 700; text-decoration: none; }
`;

const CatalogoStreamingSection = ({ config = {}, proyecto, openRegister }) => {
  const c = config;
  const { user } = useAuth();
  const navigate = useNavigate();
  const docs = useMemo(() => proyecto?.documentos || [], [proyecto]);

  // Plan de formación: con subrol especial solo sus cursos; Inactivo, ninguno.
  const mp = useMiPlan({ detalle: true });
  const subrolLocal = user?.subrol || '';
  const modoPlan = mp.listo ? mp.modo : (['', 'sin-institucion'].includes(subrolLocal) ? 'todos' : 'esperando');
  const enPlan = modoPlan === 'plan' || modoPlan === 'inactivo' || modoPlan === 'esperando';

  // Ítems con los datos del curso real cuando están enlazados.
  const itemsTodos = useMemo(() => (Array.isArray(c.items) ? c.items : []).map((it, i) => {
    const doc = it.documento_id ? docs.find(d => String(d.id) === String(it.documento_id)) : null;
    const modulos = doc?.modulos || [];
    const nLecciones = modulos.reduce((n, m) => n + (m.lecciones?.length || 0), 0);
    return {
      ...it,
      key: it.key || `item${i}`,
      titulo: it.titulo || doc?.titulo || 'Curso',
      sinopsis: it.sinopsis ? sinHtml(it.sinopsis) : primerParrafo(doc?.descripcion),
      imagen: it.imagen || doc?.imagen || '',
      docente: it.docente || doc?.docente || '',
      creado: doc?.creado || '',                         // fecha en que se subió el curso
      // Ficha: descripción completa (sin repetir la sinopsis), duración, examen y docentes.
      descripcion: (() => { const l = lineasDescripcion(it.descripcion || doc?.descripcion); const sin = it.sinopsis ? sinHtml(it.sinopsis) : primerParrafo(doc?.descripcion); return l.length === 1 && sinHtml(l[0]) === sin ? '' : l.join('\n'); })(),
      duracion_segundos: Number(it.duracion_segundos || doc?.duracion_segundos) || 0,
      examen: doc?.examen || null,
      examenes: Array.isArray(doc?.examenes) ? doc.examenes : [],
      docentes: (doc?.docentes_sel || []).length
        ? doc.docentes_sel
        : ((it.docente || doc?.docente) ? [{ nombre: it.docente || doc.docente, foto: it.docente_foto || doc?.docente_foto || '', cargo: it.docente_cargo || '' }] : []),
      modulos,
      n_modulos: modulos.length || Number(it.n_modulos) || 0,
      n_lecciones: nLecciones || Number(it.n_lecciones) || 0,
    };
  }), [c.items, docs]);
  const items = useMemo(() => {
    if (modoPlan === 'inactivo' || modoPlan === 'esperando') return [];
    if (modoPlan === 'plan') {
      const ids = (mp.cursos || []).map(String);
      // En el orden del plan; un curso del plan que no esté en el catálogo se arma con los datos del documento.
      return ids.map(id => itemsTodos.find(it => String(it.documento_id) === id)
        || (() => { const d = docs.find(x => String(x.id) === id); return d ? { key: `plan${id}`, documento_id: d.id, titulo: d.titulo, sinopsis: sinHtml(d.descripcion), imagen: d.imagen || '', modulos: d.modulos || [], n_modulos: (d.modulos || []).length, n_lecciones: (d.modulos || []).reduce((n, m) => n + (m.lecciones?.length || 0), 0) } : null; })())
        .filter(Boolean);
    }
    return itemsTodos;
  }, [itemsTodos, modoPlan, mp.cursos, docs]);
  const porKey = useMemo(() => Object.fromEntries(items.map(it => [it.key, it])), [items]);

  // Avance de los cursos enlazados (para «Continuar viendo» y la ficha).
  const [progreso, setProgreso] = useState({});
  const [lecciones, setLecciones] = useState({});   // { docId: {total, hechas} }
  const [porModulo, setPorModulo] = useState({});   // { moduloId: {total, hechas} } (ficha)
  const [ultimaConexion, setUltimaConexion] = useState({});   // { docId: 'Y-m-d H:i:s' UTC } (ficha)
  const idsDocs = items.map(it => it.documento_id).filter(Boolean).join(',');
  useEffect(() => {
    if (!user || !idsDocs) return;
    let vivo = true;
    mentor2Service.progresoProyecto(idsDocs.split(','), { porModulo: true })
      .then(res => {
        if (!vivo || !res.data?.success) return;
        setProgreso(res.data.progreso || {}); setLecciones(res.data.lecciones || {}); setPorModulo(res.data.modulos_lecciones || {}); setUltimaConexion(res.data.ultima_conexion || {});
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [user, idsDocs]);
  const avanceDe = (it) => (it.documento_id ? Number(progreso[String(it.documento_id)] || 0) : 0);

  const moneda = c.moneda || 'USD';
  const fmt = useCallback((v) => {
    const n = Number(v);
    if (!v && v !== 0) return '';
    if (Number.isNaN(n)) return String(v);
    try { return new Intl.NumberFormat('es-CO', { style: 'currency', currency: moneda, maximumFractionDigits: n % 1 ? 2 : 0 }).format(n); }
    catch (e) { return `${moneda} ${n}`; }
  }, [moneda]);

  // Qué hace el botón principal de un curso.
  const accion = useCallback((it) => {
    if (user && it.documento_id) {
      return { texto: avanceDe(it) > 0 ? (c.texto_continuar || 'Continuar') : (c.texto_ver || 'Ver curso'), icono: Ico.play, ir: () => navigate(`/mentor/${it.documento_id}`) };
    }
    if (it.url_compra) {
      return {
        texto: c.texto_comprar || 'Comprar', precio: fmt(it.precio), antes: fmt(it.precio_antes), icono: Ico.carrito,
        ir: () => { if (/^https?:\/\//i.test(it.url_compra)) window.open(it.url_compra, '_blank', 'noopener'); else navigate(it.url_compra); },
      };
    }
    if (!user) return { texto: c.texto_registro || 'Crear cuenta', icono: Ico.play, ir: () => (openRegister ? openRegister() : navigate('/register')) };
    return null;
  }, [user, c.texto_continuar, c.texto_ver, c.texto_comprar, c.texto_registro, fmt, navigate, openRegister]); // eslint-disable-line react-hooks/exhaustive-deps

  // Temario de la ficha: con sesión lleva a esa lección (si aún está bloqueada por el orden del
  // curso, el reproductor abre la siguiente disponible); sin sesión, lo mismo que el botón principal.
  const irALeccion = useCallback((it, moduloId, numLeccion) => {
    if (user && it.documento_id) {
      navigate(`/mentor/${it.documento_id}?modulo=${moduloId}${numLeccion ? `&leccion=${numLeccion}` : ''}`);
      return;
    }
    accion(it)?.ir();
  }, [user, navigate, accion]);

  // Examen en el temario: con sesión lleva al curso en su módulo (ahí se ve si ya está disponible); sin sesión, el botón principal.
  const irAExamen = useCallback((it, ex) => {
    if (user && it.documento_id) {
      navigate(`/mentor/${it.documento_id}${ex.alcance !== 'curso' && ex.modulo_id ? `?modulo=${ex.modulo_id}` : ''}`);
      return;
    }
    accion(it)?.ir();
  }, [user, navigate, accion]);
  const filaExamen = (it, ex) => {
    const detalle = [ex.preguntas ? `${ex.preguntas} ${ex.preguntas === 1 ? 'pregunta' : 'preguntas'}` : '', ex.porcentaje ? `se aprueba con ${Math.round(ex.porcentaje)} %` : ''].filter(Boolean).join(', ');
    return (
      <li key={`ex${ex.id}`} className="enlace examen">
        <button type="button" onClick={() => irAExamen(it, ex)} aria-label={`${ex.titulo}${detalle ? `: ${detalle}` : ''}`}>
          <b>{Ico.examen}</b>
          <span>{ex.titulo}{detalle && <small>{detalle.charAt(0).toUpperCase() + detalle.slice(1)}</small>}</span>
          <i aria-hidden="true">{Ico.play}</i>
        </button>
      </li>
    );
  };

  // ---------- Ficha ----------
  const [ficha, setFicha] = useState(null);
  const columnaDerRef = useRef(null);   // columna derecha de la ficha: mide hasta dónde va «Sobre el curso»
  const cerrarRef = useRef(null);
  useEffect(() => {
    if (!ficha) return undefined;
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const tecla = (e) => { if (e.key === 'Escape') setFicha(null); };
    window.addEventListener('keydown', tecla);
    setTimeout(() => cerrarRef.current?.focus(), 30);
    return () => { document.body.style.overflow = antes; window.removeEventListener('keydown', tecla); };
  }, [ficha]);

  // ---------- Vista previa al posar el cursor ----------
  const [previa, setPrevia] = useState(null);
  const temporizador = useRef(null);
  const previaRef = useRef({ abierta: false, cerradaEn: 0 });
  useEffect(() => {
    if (previa) previaRef.current.abierta = true;
    else if (previaRef.current.abierta) previaRef.current = { abierta: false, cerradaEn: Date.now() };
  }, [previa]);
  const entrar = (it, e) => {
    if (!conPuntero()) return;
    const el = e.currentTarget;
    clearTimeout(temporizador.current);
    // Pasando de una vista previa a la miniatura de al lado, la nueva sale ya.
    const seguido = previaRef.current.abierta || Date.now() - previaRef.current.cerradaEn < 600;
    temporizador.current = setTimeout(() => {
      const r = el.getBoundingClientRect();
      const ancho = Math.max(r.width * 1.5, 300);
      const vw = document.documentElement.clientWidth;
      let left = r.left + r.width / 2 - ancho / 2;
      let origen = 'center';
      if (left < 12) { left = r.left; origen = 'left'; }
      if (left + ancho > vw - 12) { left = r.right - ancho; origen = 'right'; }
      const altoImg = ancho * 9 / 16;
      const top = r.top + window.scrollY + r.height / 2 - altoImg / 2;
      setPrevia({ it, left: left + window.scrollX, top, ancho, origen: `${origen} ${Math.round(altoImg / 2)}px` });
      setEnCartelera(it);
    }, seguido ? 70 : 480);
  };
  const salir = () => clearTimeout(temporizador.current);
  useEffect(() => {
    if (!previa) return undefined;
    const cerrar = () => setPrevia(null);
    window.addEventListener('scroll', cerrar, { passive: true });
    return () => window.removeEventListener('scroll', cerrar);
  }, [previa]);
  useEffect(() => () => clearTimeout(temporizador.current), []);

  const abrirFicha = (it) => { setPrevia(null); setFicha(it); };

  // El buscador del header abre la ficha de un curso: con el evento si ya se
  // está en la portada, o con ?curso=<key> si viene de otra página.
  useEffect(() => {
    const abrir = (e) => { const it = porKey[e.detail?.key]; if (it) abrirFicha(it); };
    window.addEventListener('catalogo:abrir', abrir);
    const pedido = new URLSearchParams(window.location.search).get('curso');
    if (pedido && porKey[pedido]) {
      abrirFicha(porKey[pedido]);
      window.history.replaceState(null, '', window.location.pathname);
    }
    return () => window.removeEventListener('catalogo:abrir', abrir);
  }, [porKey]);

  // ---------- Cartelera ----------
  // Varios destacados se alternan; uno solo (o `destacado_key`) queda fijo.
  // Modo progreso (/contenidos): la cartelera muestra lo que el alumno tiene empezado.
  const modoProgreso = !!(c.modo_progreso && user);
  const destacados = useMemo(() => {
    if (modoProgreso) {
      const enCurso = items.filter(it => { const p = it.documento_id ? Number(progreso[String(it.documento_id)] || 0) : 0; return p > 0 && p < 100; })
        .sort((a, b) => Number(progreso[String(b.documento_id)] || 0) - Number(progreso[String(a.documento_id)] || 0));
      if (enCurso.length) return enCurso.slice(0, 3);
    }
    const keys = (Array.isArray(c.destacados) && c.destacados.length ? c.destacados : [c.destacado_key]).filter(k => porKey[k]);
    return keys.length ? keys.map(k => porKey[k]) : (items[0] ? [items[0]] : []);
  }, [c.destacados, c.destacado_key, porKey, items, modoProgreso, progreso]);

  // Resumen para «Mi progreso»: avance total = lecciones vistas / lecciones de los cursos empezados.
  const [panelProgreso, setPanelProgreso] = useState(false);
  const resumen = useMemo(() => {
    const conDoc = items.filter(it => it.documento_id);
    const det = (it) => lecciones[String(it.documento_id)] || { total: it.n_lecciones || 0, hechas: 0 };
    const empezados = conDoc.filter(it => Number(progreso[String(it.documento_id)] || 0) > 0);
    const terminados = empezados.filter(it => Number(progreso[String(it.documento_id)] || 0) >= 100);
    const vistas = empezados.reduce((n, it) => n + (det(it).hechas || 0), 0);
    const total = empezados.reduce((n, it) => n + (det(it).total || 0), 0);
    return {
      empezados: empezados.sort((a, b) => Number(progreso[String(b.documento_id)] || 0) - Number(progreso[String(a.documento_id)] || 0)),
      porEmpezar: conDoc.filter(it => !(Number(progreso[String(it.documento_id)] || 0) > 0)),
      terminados: terminados.length, vistas, pct: total ? Math.round((vistas / total) * 100) : 0, det,
    };
  }, [items, progreso, lecciones]);
  // Avance de un curso con sesión: «Llevas X % · h de t lecciones» + barra por lecciones.
  const avanceCurso = (it, { alto = 7, ancho = '24rem', margen = '-.2rem 0 1.2rem', tam = '.95rem', vidrio = true } = {}) => {
    const p = Math.round(avanceDe(it));
    if (!user || !it?.documento_id || p <= 0) return null;
    const d = resumen.det(it);
    return (
      <div style={{
        maxWidth: ancho, margin: margen,
        // En el banner, vidrio oscuro: se lee sobre cualquier foto.
        ...(vidrio ? { padding: '.7rem .9rem .8rem', borderRadius: 10, background: 'rgba(8,10,14,.55)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.12)' } : {}),
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBottom: '.45rem', fontSize: tam }}>
          <strong style={{ color: vidrio ? '#fff' : 'var(--acento)' }}>{p >= 100 ? 'Curso terminado' : `Llevas ${p} %`}</strong>
          {d.total > 0 && <span style={{ color: 'rgba(255,255,255,.75)', fontVariantNumeric: 'tabular-nums' }}>{d.hechas} de {d.total} lecciones</span>}
        </div>
        <BarraLecciones hechas={d.hechas} total={d.total} pct={p} alto={alto} />
      </div>
    );
  };

  useEffect(() => {
    if (!panelProgreso) return undefined;
    const tecla = (e) => { if (e.key === 'Escape') setPanelProgreso(false); };
    window.addEventListener('keydown', tecla);
    const antes = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', tecla); document.body.style.overflow = antes; };
  }, [panelProgreso]);
  const [idxDest, setIdxDest] = useState(0);
  const [pausa, setPausa] = useState(false);
  const destacado = destacados[idxDest % Math.max(1, destacados.length)];
  const durCartelera = (Number(c.segundos_cartelera) || 9) * 1000;
  // Curso de la miniatura sobre la que se posó el cursor: ocupa la cartelera
  // (portada, nombre, descripción corta y enlace) hasta que se pase a otra
  // miniatura o el cursor salga del catálogo; mientras tanto no rota.
  const [enCartelera, setEnCartelera] = useState(null);
  useEffect(() => {
    if (destacados.length < 2 || pausa || enCartelera || reduceMovimiento()) return undefined;
    const t = setTimeout(() => setIdxDest(i => (i + 1) % destacados.length), durCartelera);
    return () => clearTimeout(t);
  }, [idxDest, destacados.length, pausa, enCartelera, durCartelera]);

  // Filtro por especialidad y preguntas abiertas.
  const especialidades = useMemo(() => [...new Set(items.map(it => (it.especialidad || '').trim()).filter(Boolean))], [items]);
  const [filtro, setFiltro] = useState('');
  // «Ver todos» de una fila: la muestra entera en cuadrícula.
  const [todasDe, setTodasDe] = useState(null);
  const filasRef = useRef(null);
  const verTodas = (id) => {
    setTodasDe(id);
    requestAnimationFrame(() => filasRef.current?.scrollIntoView({ behavior: reduceMovimiento() ? 'auto' : 'smooth', block: 'start' }));
  };
  const [abierta, setAbierta] = useState(null);

  const pintarMeta = (it, { precio = true } = {}) => (
    <>
      {it.n_modulos > 0 && <span>{it.n_modulos} {it.n_modulos === 1 ? 'módulo' : 'módulos'}</span>}
      {it.n_lecciones > 0 && <span>{it.n_lecciones} {it.n_lecciones === 1 ? 'lección' : 'lecciones'}</span>}
      {precio && !user && it.precio !== undefined && it.precio !== '' && <strong>{fmt(it.precio)}</strong>}
    </>
  );

  // Inclinación 3D del Top 10: el giro y el brillo siguen al cursor.
  const inclinar = (e) => {
    if (reduceMovimiento()) return;
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    el.style.setProperty('--ry', `${((x - 0.5) * 16).toFixed(2)}deg`);
    el.style.setProperty('--rx', `${((0.5 - y) * 12).toFixed(2)}deg`);
    el.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
    el.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
  };
  const enderezar = (e) => {
    ['--rx', '--ry'].forEach(v => e.currentTarget.style.setProperty(v, '0deg'));
  };

  const tarjeta = (it, { vertical = false, fila, top = false, numero = 0 } = {}) => {
    const pct = avanceDe(it);
    const eventos = top
      ? { onMouseMove: inclinar, onMouseLeave: enderezar, onMouseEnter: () => { if (conPuntero()) setEnCartelera(it); } }
      : { onMouseEnter: (e) => entrar(it, e), onMouseLeave: salir, onFocus: () => setPrevia(null) };
    return (
      <Miniatura
        key={`${fila}-${it.key}`}
        type="button"
        $vertical={vertical}
        {...eventos}
        onClick={() => abrirFicha(it)}
        aria-label={`${it.titulo}. Ver ficha del curso`}
      >
        {/* La cinta «Nuevo» solo en Novedades (las tarjetas numeradas). */}
        {numero > 0 && <Cinta>{c.texto_nuevo || 'Nuevo'}</Cinta>}
        {numero > 0 && <Numero aria-hidden="true">{numero}</Numero>}
        {/* Con número, el nombre siempre abajo a la izquierda: arriba está la muesca. */}
        <Portada item={numero > 0 ? { ...it, posicion_titulo: 'abajo' } : it} vertical={vertical} conTitulo />
        {user && pct > 0 && <Avance $pct={Math.min(100, pct)}><i /></Avance>}
        {user && pct > 0 && <PctMini>{pct >= 100 ? '✓ Terminado' : `${Math.round(pct)} %`}</PctMini>}
        {top && (
          <Revelar aria-hidden="true">
            {user && pct > 0
              ? <em>{Math.round(pct)} % visto</em>
              : (it.precio !== undefined && it.precio !== '' ? <em>{fmt(it.precio)}</em> : <span />)}
            <span>{c.texto_info_corto || 'Ver ficha'}</span>
          </Revelar>
        )}
      </Miniatura>
    );
  };

  // Filas configuradas. Sin filas se arma una con todos los cursos. Con plan de formación,
  // una sola: «Mis cursos obligatorios».
  const filasConfig = modoPlan === 'plan'
    ? [{ id: 'obligatorios', titulo: c.texto_obligatorios || 'Mis cursos obligatorios', tipo: 'todos' }]
    : (Array.isArray(c.filas) && c.filas.length ? c.filas : [{ titulo: 'Todos los cursos', tipo: 'todos' }]);
  const filas = filasConfig
    .map((f, i) => {
      let lista = [];
      if (f.tipo === 'continuar') lista = user ? items.filter(it => { const p = avanceDe(it); return p > 0 && p < 100; }) : [];
      else if (f.tipo === 'especialidad') lista = items.filter(it => (it.especialidad || '').toLowerCase() === (f.especialidad || '').toLowerCase());
      else if (f.tipo === 'titulo') lista = [];
      else if (f.tipo === 'novedades') {
        lista = items.filter(it => it.documento_id && it.creado)
          .sort((a, b) => String(b.creado).localeCompare(String(a.creado)))
          .slice(0, 10);
      } else if (f.tipo === 'categoria') {
        const cat = (f.categoria || '').trim().toLowerCase();
        lista = items.filter(it => {
          const suya = (it.categoria || '').trim().toLowerCase();
          return (cat && suya === cat) || (f.sin_categoria && !suya);
        });
      }
      else if (f.tipo === 'todos' || !Array.isArray(f.items) || !f.items.length) lista = items;
      else lista = f.items.map(k => porKey[k]).filter(Boolean);
      if (f.tipo === 'top10') lista = lista.slice(0, 10);
      return { ...f, id: f.id || `fila${i}`, lista };
    })
    .filter(f => f.tipo === 'titulo' || f.lista.length)
    // Un título sin filas con cursos debajo no se pinta.
    .filter((f, i, todas) => f.tipo !== 'titulo' || (todas[i + 1] && todas[i + 1].tipo !== 'titulo'));

  if (modoPlan === 'esperando') return <Raiz $fondo={c.color_fondo || '#060b14'} $acento={c.color_acento || '#5ee0d2'} $texto={c.color_texto || '#f2f5f9'} $tenue={c.color_tenue || '#8a97ab'} style={{ minHeight: '100vh' }} />;
  if (enPlan && !items.length) {
    const inactivo = modoPlan === 'inactivo';
    return (
      <Raiz $fondo={c.color_fondo || '#060b14'} $acento={c.color_acento || '#5ee0d2'} $sobreAcento={c.color_texto_acento || '#04201d'} $texto={c.color_texto || '#f2f5f9'} $tenue={c.color_tenue || '#8a97ab'}>
        <AvisoPlan role="status">
          <h2>{inactivo ? 'Tu acceso está inactivo' : 'Aún no tienes cursos asignados'}</h2>
          <p>{inactivo
            ? 'Renueva tu suscripción o escríbenos para reactivarlo. Tu avance y tus certificados siguen guardados.'
            : 'Te avisaremos cuando estén disponibles los cursos de tu plan de formación.'}</p>
          {inactivo && <a href="/contactanos?tema=cuenta">Escríbenos</a>}
        </AvisoPlan>
      </Raiz>
    );
  }
  if (!items.length) return null;
  const enFoco = enCartelera || destacado;              // lo que muestra la cartelera
  const principal = enFoco ? accion(enFoco) : null;
  const largo = (enFoco?.titulo_cartelera || enFoco?.titulo || '').length > 34;

  return (
    <Raiz
      $fondo={c.color_fondo || '#060b14'}
      $acento={c.color_acento || '#5ee0d2'}
      $sobreAcento={c.color_texto_acento || '#04201d'}
      $texto={c.color_texto || '#f2f5f9'}
      $tenue={c.color_tenue || '#8a97ab'}
      onMouseLeave={() => setEnCartelera(null)}
    >
      {destacado && (
        <Cartelera onFocus={() => setPausa(true)} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setPausa(false); }}>
          <Fondo>
            {destacados.map((d, k) => (
              <div key={d.key} className={`capa${!enCartelera && d.key === destacado.key ? ' on' : ''}`}>
                {urlImg(d.imagen_cartelera || d.imagen)
                  ? <img src={urlImg(d.imagen_cartelera || d.imagen)} alt="" key={d.key === destacado.key ? `on${idxDest}` : 'off'}
                      style={d.enfoque_cartelera ? { objectPosition: d.enfoque_cartelera } : undefined} />
                  : <PosterGenerado $tono={d.color || tonoDe(d.especialidad || d.titulo)} />}
              </div>
            ))}
            {enCartelera && (
              <div key={`hover-${enCartelera.key}`} className="capa on">
                {urlImg(enCartelera.imagen_cartelera || enCartelera.imagen)
                  ? <img src={urlImg(enCartelera.imagen_cartelera || enCartelera.imagen)} alt=""
                      style={enCartelera.enfoque_cartelera ? { objectPosition: enCartelera.enfoque_cartelera } : undefined} />
                  : <PosterGenerado $tono={enCartelera.color || tonoDe(enCartelera.especialidad || enCartelera.titulo)} />}
              </div>
            )}
          </Fondo>
          {enCartelera ? (
          <CarteleraTexto key={`hover-${enCartelera.key}`}>
            <TituloCartelera $largo={largo}>{enCartelera.titulo_cartelera || enCartelera.titulo}</TituloCartelera>
            {avanceCurso(enCartelera, { margen: '.2rem 0 1.2rem' })}
            {enCartelera.sinopsis && <Sinopsis>{enCartelera.sinopsis}</Sinopsis>}
            <Botones>
              {principal && (
                <Boton $primario type="button" onClick={principal.ir}>
                  {principal.icono}{principal.texto}
                </Boton>
              )}
            </Botones>
          </CarteleraTexto>
          ) : (
          <CarteleraTexto key={destacado.key}>
            {destacado.especialidad && <Especialidad>{destacado.especialidad}</Especialidad>}
            <TituloCartelera $largo={largo}>{destacado.titulo_cartelera || destacado.titulo}</TituloCartelera>
            <Meta>
              {pintarMeta(destacado, { precio: false })}
              {!user && destacado.precio !== undefined && destacado.precio !== '' && (
                <strong>{fmt(destacado.precio)}{destacado.precio_antes ? <s style={{ marginLeft: '.5rem', fontWeight: 400, opacity: .6 }}>{fmt(destacado.precio_antes)}</s> : null}</strong>
              )}
            </Meta>
            {avanceCurso(destacado)}
            {destacado.sinopsis && <Sinopsis>{destacado.sinopsis}</Sinopsis>}
            <Botones>
              {principal && (
                <Boton $primario type="button" onClick={principal.ir}>
                  {principal.icono}
                  {principal.precio ? `${principal.texto} por ${principal.precio}` : principal.texto}
                  {principal.antes && <small>{principal.antes}</small>}
                </Boton>
              )}
              <Boton type="button" onClick={() => abrirFicha(destacado)}>{Ico.info}{c.texto_info || 'Contenido'}</Boton>
              {user && resumen.empezados.length > 0 && (
                <BotonProgreso type="button" onClick={() => setPanelProgreso(true)} aria-haspopup="dialog">
                  <span className="anillo"><Anillo pct={resumen.pct} tam={34} /><b>{resumen.pct}%</b></span>
                  <span>{c.texto_progreso || 'Mi progreso'}<small>{resumen.empezados.length} {resumen.empezados.length === 1 ? 'curso' : 'cursos'} en marcha</small></span>
                </BotonProgreso>
              )}
            </Botones>
            {destacados.length > 1 && (
              <Indicadores style={{ '--dur': `${durCartelera}ms`, '--estado': pausa ? 'paused' : 'running' }}>
                {destacados.map((d, k) => (
                  <button key={d.key} type="button" aria-label={`Ver ${d.titulo_cartelera || d.titulo}`}
                    aria-current={k === idxDest ? 'true' : undefined} className={k < idxDest ? 'visto' : ''}
                    onClick={() => setIdxDest(k)}><i key={k === idxDest ? `a${idxDest}` : 'b'} /></button>
                ))}
              </Indicadores>
            )}
          </CarteleraTexto>
          )}
          {!enCartelera && (
          <Lateral>
            {(destacado.aval || c.texto_certificado) && <Aval>{destacado.aval || c.texto_certificado}</Aval>}
          </Lateral>
          )}
        </Cartelera>
      )}

      <Filas ref={filasRef}>
        {modoPlan === 'plan' && mp.avance && (
          <PlanAvance href="/mi-plan" onClick={(e) => { e.preventDefault(); navigate('/mi-plan'); }}>
            <span className="anillo"><Anillo pct={mp.avance.pct} tam={58} grosor={5} /><b>{mp.avance.pct}%</b></span>
            <span>
              <strong>Mi avance en el plan de formación</strong>
              <span className="det">
                {mp.avance.hechas} de {mp.avance.total} lecciones · {mp.avance.pct >= 100 ? 'completaste tu plan' : `te falta el ${100 - mp.avance.pct} % para tu certificado final`}
              </span>
            </span>
            <span className="ir">Ver mi plan</span>
          </PlanAvance>
        )}

        {modoPlan !== 'plan' && c.mostrar_especialidades !== false && especialidades.length > 1 && (
          <Chips role="toolbar" aria-label="Filtrar por especialidad">
            <button type="button" aria-pressed={!filtro} onClick={() => setFiltro('')}>{c.texto_todas || 'Todo'}</button>
            {especialidades.map(e => (
              <button key={e} type="button" aria-pressed={filtro === e} onClick={() => setFiltro(filtro === e ? '' : e)}>{e}</button>
            ))}
          </Chips>
        )}
        {filtro && (() => {
          const lista = items.filter(it => (it.especialidad || '').trim() === filtro);
          return (
            <>
              <TituloFiltro><h2>{filtro}</h2><span>{lista.length} {lista.length === 1 ? 'curso' : 'cursos'}</span></TituloFiltro>
              <Rejilla>{lista.map(it => tarjeta(it, { fila: 'filtro' }))}</Rejilla>
            </>
          );
        })()}
        {!filtro && todasDe && (() => {
          const f = filas.find(x => x.id === todasDe);
          if (!f) return null;
          return (
            <>
              <TituloFiltro>
                <button type="button" onClick={() => setTodasDe(null)}>{Ico.izq}Volver</button>
                <h2>{f.titulo}</h2>
                <span>{f.lista.length} {f.lista.length === 1 ? 'curso' : 'cursos'}</span>
              </TituloFiltro>
              <Rejilla>{f.lista.map((it, k) => tarjeta(it, { fila: 'todas', numero: f.tipo === 'novedades' ? k + 1 : 0 }))}</Rejilla>
            </>
          );
        })()}
        {!filtro && !todasDe && filas.map(f => (f.tipo === 'titulo' ? (
          <TituloGrupo key={f.id}>{f.titulo}</TituloGrupo>
        ) : (
          <Fila key={f.id} titulo={f.titulo} top={f.tipo === 'top10'}
            etiqueta={f.etiqueta || ''} enVivo={f.tipo === 'novedades'}
            onVerTodos={f.tipo === 'top10' ? undefined : () => verTodas(f.id)}>
            {f.tipo === 'novedades'
              ? f.lista.map((it, k) => tarjeta(it, { fila: f.id, numero: k + 1 }))
              : f.tipo === 'top10'
              ? f.lista.map((it, k) => (
                <TopItem key={`${f.id}-${it.key}`} $doble={k >= 9}>
                  <b aria-hidden="true">{k + 1}</b>
                  {tarjeta(it, { vertical: true, fila: f.id, top: true })}
                </TopItem>
              ))
              : f.lista.map(it => tarjeta(it, { fila: f.tipo === 'continuar' ? 'continuar' : f.id }))}
          </Fila>
        )))}
      </Filas>

      {!user && Array.isArray(c.preguntas) && c.preguntas.length > 0 && (
        <Preguntas aria-label={c.titulo_preguntas || 'Preguntas frecuentes'}>
          <h2>{c.titulo_preguntas || 'Preguntas frecuentes'}</h2>
          {c.preguntas.map((q, k) => (
            <Pregunta key={k} data-abierta={abierta === k ? 1 : 0}>
              <button type="button" aria-expanded={abierta === k} onClick={() => setAbierta(abierta === k ? null : k)}>
                {q.pregunta}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M12 4v16M4 12h16" strokeLinecap="round" /></svg>
              </button>
              <div><p>{q.respuesta}</p></div>
            </Pregunta>
          ))}
        </Preguntas>
      )}

      {!user && c.cierre_titulo && (
        <Cierre>
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0 70h380l18-18 20 18h40l14 28 26-96 24 110 16-42 14 18h60l24-26 26 26h520" />
          </svg>
          <h2>{c.cierre_titulo}</h2>
          {c.cierre_texto && <p>{c.cierre_texto}</p>}
          <Boton $primario type="button" onClick={() => (openRegister ? openRegister() : navigate('/register'))}>
            {c.cierre_boton || c.texto_registro || 'Crear cuenta'}
          </Boton>
        </Cierre>
      )}

      {previa && createPortal(
        <Previa
          style={{ left: previa.left, top: previa.top, width: previa.ancho, '--acento': c.color_acento || '#5ee0d2', '--sobre-acento': c.color_texto_acento || '#04201d' }}
          $origen={previa.origen}
          onMouseLeave={() => setPrevia(null)}
        >
          <PreviaImagen>
            {previa.it.trailer && !reduceMovimiento()
              ? <video src={urlImg(previa.it.trailer)} muted autoPlay loop playsInline poster={urlImg(previa.it.imagen)} />
              : <Portada item={previa.it} />}
          </PreviaImagen>
          <PreviaCuerpo>
            <FilaBotones>
              {(() => {
                const a = accion(previa.it);
                return a ? <BotonPrevia $lleno type="button" onClick={a.ir}>{a.icono}{a.texto}</BotonPrevia> : null;
              })()}
              <BotonPrevia type="button" onClick={() => abrirFicha(previa.it)}>{Ico.abajo}{c.texto_info || 'Contenido'}</BotonPrevia>
            </FilaBotones>
            <h3>{previa.it.titulo}</h3>
            <Datos>
              {!user && previa.it.precio !== undefined && previa.it.precio !== '' && <em>{fmt(previa.it.precio)}</em>}
              {!user && previa.it.precio_antes && <s>{fmt(previa.it.precio_antes)}</s>}
              {pintarMeta(previa.it, { precio: false })}
            </Datos>
            {previa.it.especialidad && <Datos style={{ marginTop: '.45rem', color: '#e3e8ef' }}>{previa.it.especialidad}</Datos>}
            {avanceCurso(previa.it, { alto: 5, ancho: 'none', margen: '.75rem 0 0', tam: '.8rem', vidrio: false })}
          </PreviaCuerpo>
        </Previa>,
        document.body,
      )}

      {panelProgreso && createPortal(
        <VeloPanel onClick={(e) => { if (e.target === e.currentTarget) setPanelProgreso(false); }}
          style={{ '--acento': c.color_acento || '#5ee0d2', '--sobre-acento': c.color_texto_acento || '#04201d', '--fondo': c.color_fondo || '#060b14', '--texto': c.color_texto || '#f2f5f9', '--tenue': c.color_tenue || '#8a97ab', '--superficie': 'rgba(255,255,255,.06)', fontFamily: FUENTE }}>
          <Panel role="dialog" aria-modal="true" aria-label="Mi progreso">
            <CerrarPanel type="button" onClick={() => setPanelProgreso(false)} aria-label="Cerrar" autoFocus>{Ico.x}</CerrarPanel>
            <div className="cab">
              <span className="grande"><Anillo pct={resumen.pct} tam={92} grosor={8} /><b>{resumen.pct}%</b></span>
              <div>
                <h2>Tu progreso</h2>
                <div className="stats">
                  <span><strong>{resumen.empezados.length}</strong> en marcha</span>
                  <span><strong>{resumen.terminados}</strong> {resumen.terminados === 1 ? 'terminado' : 'terminados'}</span>
                  <span><strong>{resumen.vistas}</strong> {resumen.vistas === 1 ? 'lección vista' : 'lecciones vistas'}</span>
                </div>
              </div>
            </div>
            <h3>Tus cursos</h3>
            {resumen.empezados.length === 0 && <p className="vacio">Todavía no empiezas ningún curso. Elige uno abajo y tu avance aparecerá aquí.</p>}
            {resumen.empezados.map(it => {
              const p = Math.round(avanceDe(it)); const d = resumen.det(it);
              return (
                <FilaCurso key={it.key}>
                  <span className="foto">{urlImg(it.imagen) && <img src={urlImg(it.imagen)} alt="" loading="lazy" />}</span>
                  <span>
                    <strong>{it.titulo}</strong>
                    <span className="barra"><BarraLecciones hechas={d.hechas} total={d.total} pct={p} /></span>
                    <span className="pie">
                      <span>{p >= 100 ? 'Terminado' : `${p} %`}{d.total ? ` · ${d.hechas} de ${d.total} lecciones` : ''}</span>
                      <button type="button" onClick={() => { setPanelProgreso(false); navigate(`/mentor/${it.documento_id}`); }}>{p >= 100 ? 'Repasar' : 'Continuar'}</button>
                    </span>
                  </span>
                </FilaCurso>
              );
            })}
            {resumen.porEmpezar.length > 0 && <h3>Por empezar</h3>}
            {resumen.porEmpezar.map(it => (
              <FilaCurso key={it.key}>
                <span className="foto">{urlImg(it.imagen) && <img src={urlImg(it.imagen)} alt="" loading="lazy" />}</span>
                <span>
                  <strong>{it.titulo}</strong>
                  <span className="pie" style={{ marginTop: '.4rem' }}>
                    <span>{it.n_lecciones ? `${it.n_lecciones} lecciones` : ''}</span>
                    <button type="button" onClick={() => { setPanelProgreso(false); navigate(`/mentor/${it.documento_id}`); }}>Empezar</button>
                  </span>
                </span>
              </FilaCurso>
            ))}
            <div style={{ height: '2rem' }} />
          </Panel>
        </VeloPanel>,
        document.body
      )}

      {ficha && createPortal(
        <Velo onClick={(e) => { if (e.target === e.currentTarget) setFicha(null); }} style={{ '--acento': c.color_acento || '#5ee0d2', '--sobre-acento': c.color_texto_acento || '#04201d' }}>
          <Ficha role="dialog" aria-modal="true" aria-label={ficha.titulo}>
            <Cerrar ref={cerrarRef} type="button" onClick={() => setFicha(null)} aria-label="Cerrar">{Ico.x}</Cerrar>
            <FichaPortada>
              {ficha.trailer && !reduceMovimiento()
                ? <video src={urlImg(ficha.trailer)} muted autoPlay loop playsInline poster={urlImg(ficha.imagen_cartelera || ficha.imagen)} />
                : (urlImg(ficha.imagen_cartelera || ficha.imagen)
                  ? <img src={urlImg(ficha.imagen_cartelera || ficha.imagen)} alt="" />
                  : <PosterGenerado $tono={ficha.color || tonoDe(ficha.especialidad || ficha.titulo)} />)}
              <div className="txt">
                <h2>{ficha.titulo_cartelera || ficha.titulo}</h2>
                <Botones style={{ justifyContent: 'flex-start' }}>
                  {(() => {
                    const a = accion(ficha);
                    return a ? <Boton $primario type="button" onClick={a.ir}>{a.icono}{a.texto}</Boton> : null;
                  })()}
                </Botones>
              </div>
            </FichaPortada>
            <FichaCuerpo>
              <div>
                <Datos style={{ fontSize: '.98rem' }}>
                  {pintarMeta(ficha, { precio: false })}
                </Datos>
                {ficha.sinopsis && <p className="sinopsis">{ficha.sinopsis}</p>}
                {!user && ficha.precio !== undefined && ficha.precio !== '' && (
                  <Precio>
                    <b>{fmt(ficha.precio)}</b>
                    {ficha.precio_antes && <s>{fmt(ficha.precio_antes)}</s>}
                    {ficha.nota_precio && <span>{ficha.nota_precio}</span>}
                  </Precio>
                )}
              </div>
              {(() => {
                const det = [
                  ficha.duracion_segundos > 0 && ['Duración', duracionTexto(ficha.duracion_segundos)],
                  ficha.nivel && ['Nivel', ficha.nivel],
                  (ficha.especialidad || ficha.categoria) && ['Área', [ficha.categoria, ficha.especialidad].filter(Boolean).join(' · ')],
                  ficha.horas && ['Horas', `${ficha.horas} h`],
                ].filter(Boolean);
                if (!ficha.descripcion && !det.length && !ficha.docentes.length && !ficha.documento_id) return null;
                // Encima de «Detalles»: portada con el título, avance (en cero si no lo ha empezado) y última conexión.
                const avance = ficha.documento_id ? (() => {
                  const p = user ? Math.round(avanceDe(ficha)) : 0;
                  const d = (user && lecciones[String(ficha.documento_id)]) || { total: ficha.n_lecciones || 0, hechas: 0 };
                  const uc = user ? ultimaConexion[String(ficha.documento_id)] : null;
                  return (
                    <TuAvance className="con-portada">
                      {ficha.imagen && <div className="foto" style={{ backgroundImage: `url(${urlImg(ficha.imagen)})` }}><b>{ficha.titulo}</b></div>}
                      <div className="linea">
                        <strong>{p >= 100 ? 'Curso terminado' : p > 0 ? `Llevas ${p} %` : 'Progreso: 0 %'}</strong>
                        {d.total > 0 && <span>{d.hechas} de {d.total} {d.total === 1 ? 'lección' : 'lecciones'}</span>}
                      </div>
                      <div><BarraLecciones hechas={d.hechas} total={d.total} pct={p} alto={8} siguiente={d.hechas > 0} /></div>
                      <small>{uc ? `Última conexión: ${fmtFechaHoraBogota(uc, { hour12: true })}` : user ? 'Aún no has comenzado este curso' : 'Inicia sesión para ver tu avance'}</small>
                    </TuAvance>
                  );
                })() : null;
                return (
                  <FichaInfo>
                    <div>
                      {ficha.descripcion && (<><h3>Sobre el curso</h3><DescripcionCurso key={ficha.key} texto={ficha.descripcion} columnaRef={columnaDerRef} video={ficha.video_descripcion} /></>)}
                    </div>
                    <div ref={columnaDerRef}>
                      {avance}
                      {det.length > 0 && (
                        <>
                          <h3>Detalles</h3>
                          <Detalles>{det.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</Detalles>
                        </>
                      )}
                      {ficha.docentes.map(dc => (
                        <DocenteFicha key={dc.id || dc.nombre} className={sinHtml(dc.descripcion) ? 'con-perfil' : ''}>
                          <span className="foto">{dc.foto ? <img src={urlImg(dc.foto)} alt="" /> : (dc.nombre || '?').replace(/^(Dra?\.?|Mg\.?|MD\.?)\s+/i, '').charAt(0)}</span>
                          <span>
                            <small>{ficha.docentes.length > 1 ? 'Docente' : 'Docente del curso'}</small><b>{dc.nombre}</b>{dc.cargo && <span>{dc.cargo}</span>}
                            {sinHtml(dc.descripcion) && <p className="perfil">{sinHtml(dc.descripcion)}</p>}
                          </span>
                        </DocenteFicha>
                      ))}
                    </div>
                  </FichaInfo>
                );
              })()}
              {ficha.modulos.length > 0 && (
                <Temario>
                  <h3>Temario</h3>
                  {ficha.modulos.map(m => {
                    // Módulo de una sola lección con el mismo nombre: una fila directa, sin encabezado repetido.
                    const nrm = (t) => String(t || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '');
                    const ls = m.lecciones || [];
                    if (ls.length === 1 && nrm(ls[0].titulo) === nrm(m.titulo)) {
                      return (
                        <div key={m.id}>
                          <ol className="directo">
                            <li className="enlace">
                              <button type="button" onClick={() => irALeccion(ficha, m.id, 1)}
                                aria-label={user && ficha.documento_id ? `Ir a ${ls[0].titulo}` : `${ls[0].titulo}: ${accion(ficha)?.texto || 'ver el curso'}`}>
                                <b aria-hidden="true" /><span>{ls[0].titulo}</span><i aria-hidden="true">{Ico.play}</i>
                              </button>
                            </li>
                            {ficha.examenes.filter(ex => (ex.alcance === 'modulo' && String(ex.modulo_id) === String(m.id)) || (ex.alcance === 'leccion' && String(ex.leccion_id) === String(ls[0].id))).map(ex => filaExamen(ficha, ex))}
                          </ol>
                        </div>
                      );
                    }
                    return (
                    <div key={m.id}>
                      <h4>
                        <span><button type="button" onClick={() => irALeccion(ficha, m.id, 0)}>{m.titulo}</button></span>
                        {user && porModulo[String(m.id)] ? (
                          <span className="avance-mod">
                            {porModulo[String(m.id)].hechas} de {porModulo[String(m.id)].total}
                            <i><b style={{ width: `${porModulo[String(m.id)].total ? Math.round(100 * porModulo[String(m.id)].hechas / porModulo[String(m.id)].total) : 0}%` }} /></i>
                          </span>
                        ) : (
                          <span>{(m.lecciones || []).length} {(m.lecciones || []).length === 1 ? 'lección' : 'lecciones'}</span>
                        )}
                      </h4>
                      <ol>
                        {(m.lecciones || []).map((l, k) => (
                          <React.Fragment key={l.id}>
                            <li className="enlace">
                              <button type="button" onClick={() => irALeccion(ficha, m.id, k + 1)}
                                aria-label={user && ficha.documento_id ? `Ir a la lección ${k + 1}: ${l.titulo}` : `${l.titulo}: ${accion(ficha)?.texto || 'ver el curso'}`}>
                                <b>{k + 1}</b><span>{l.titulo}</span><i aria-hidden="true">{Ico.play}</i>
                              </button>
                            </li>
                            {ficha.examenes.filter(ex => ex.alcance === 'leccion' && String(ex.leccion_id) === String(l.id)).map(ex => filaExamen(ficha, ex))}
                          </React.Fragment>
                        ))}
                        {ficha.examenes.filter(ex => ex.alcance === 'modulo' && String(ex.modulo_id) === String(m.id)).map(ex => filaExamen(ficha, ex))}
                      </ol>
                    </div>
                    );
                  })}
                  {ficha.examenes.some(ex => ex.alcance === 'curso') && (
                    <div>
                      <h4 className="final"><span>Evaluación del curso</span></h4>
                      <ol>{ficha.examenes.filter(ex => ex.alcance === 'curso').map(ex => filaExamen(ficha, ex))}</ol>
                    </div>
                  )}
                </Temario>
              )}
              {!ficha.modulos.length && Array.isArray(ficha.temario) && ficha.temario.length > 0 && (
                <Temario>
                  <h3>Temario</h3>
                  <ol>{ficha.temario.map((t, k) => <li key={k}><b>{k + 1}</b><span>{t}</span></li>)}</ol>
                </Temario>
              )}
            </FichaCuerpo>
          </Ficha>
        </Velo>,
        document.body,
      )}
    </Raiz>
  );
};

export default CatalogoStreamingSection;
